import React, { useState, useEffect, useRef } from "react";
import { View, Text, StyleSheet, Pressable, Picker, TextInput, Image, Linking, Dimensions, Platform, ScrollView, TouchableOpacity, Animated, Alert, useWindowDimensions } from "react-native";
import useEventsWidget, { EventsEditor } from './EventsWidget';

import { uploadBytes, getDownloadURL, ref, getStorage } from "firebase/storage";
import { getFirestore, doc, getDoc, setDoc, updateDoc, query, where, getDocs, collection } from "firebase/firestore";
import { getAuth, signInAnonymously } from "firebase/auth";
import ImageGallery from "../Screens/EventsWidgets/ImageGallery"; // Adjust the path as necessary
import Countdown from "../Screens/EventsWidgets/Countdown";
import Poll from "./Poll";

// Use expo-image-picker for Expo apps (works on both mobile and web)
import * as ImagePicker from 'expo-image-picker';

import DateTimePicker from '@react-native-community/datetimepicker'; // Import a date-time picker library
import PromptSignUp from './PromptSignUp'; // Import the PromptSignUp component
import SharedModal from './SharedModal';
import { useUser } from "./UserContext";
import { handleAutoResubscribe } from '../services/AutoResubscribeService';
import genericUserPhoto from '../assets/generic.jpg';
import { Ionicons } from '@expo/vector-icons';
import { validateEventCard, wouldOverwriteValidCard, safeEventCardMerge } from '../services/WidgetWriteGuard';
import { logWidgetEvent } from '../services/WidgetAuditLogger';
import { MYTASSEL_PLUS_FEATURES } from '../constants/MyTasselPlus';
import { getAvailableWidgetOptions } from '../constants/WidgetTypes';
import { spotlightTarget, spotlightTargetWhen, widgetOptionTarget, SPOTLIGHT_TARGETS } from './SpotlightTutorial/spotlightTargets';
import { emitSpotlightEvent, useEmitSpotlightEventWhenOpened } from './SpotlightTutorial/spotlightBus';
import { SPOTLIGHT_EVENTS } from './SpotlightTutorial/spotlightEventNames';
import useMyTasselPlus from '../Hooks/useMyTasselPlus';
const { width, height } = Dimensions.get('window');
const isMobile = width < 768;
const isTablet = width >= 768 && width < 1024;
const isSmallDesktop = width >= 1024 && width < 1440;
const isLargeDesktop = width >= 1440;

// ===== Event Photo Slideshow Component =====
const EventPhotoSlideshow = ({ photos }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!photos || photos.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % photos.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [photos]);

  if (!photos || photos.length === 0) return null;

  return (
    <View style={{ marginTop: 16, borderRadius: 14, overflow: 'hidden', borderWidth: 1, borderColor: 'rgba(76, 149, 108, 0.12)' }}>
      <View style={{ width: '100%', aspectRatio: 16 / 9, position: 'relative', backgroundColor: '#f5f0e1' }}>
        {photos.map((photo, idx) => (
          <Image
            key={idx}
            source={{ uri: photo }}
            style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              width: '100%',
              height: '100%',
              opacity: idx === currentIndex ? 1 : 0,
              transition: 'opacity 0.6s ease-in-out',
            }}
            resizeMode="cover"
          />
        ))}
        {/* Dot indicators */}
        {photos.length > 1 && (
          <View style={{ position: 'absolute', bottom: 10, left: 0, right: 0, flexDirection: 'row', justifyContent: 'center', gap: 6 }}>
            {photos.map((_, idx) => (
              <Pressable
                key={idx}
                onPress={() => setCurrentIndex(idx)}
                style={{
                  width: idx === currentIndex ? 20 : 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: idx === currentIndex ? '#fff' : 'rgba(255,255,255,0.5)',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </View>
        )}
      </View>
    </View>
  );
};

let LOCAL_SERVER_URL = "http://localhost:5001/my-tassel/us-central1/api";
let FIREBASE_SERVER_URL = "https://us-central1-my-tassel.cloudfunctions.net/api";
// Always use production Firebase Functions (no emulators)
const SERVER_URL = FIREBASE_SERVER_URL;


// Add this function at the top of your file
const fetchMetadata = async (url) => {
  const formattedUrl = url.startsWith("http") ? url : `https://${url}`;
  try {
    const response = await fetch(`${SERVER_URL}/fetch-metadata`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url: formattedUrl }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch metadata: ${response.statusText}`);
    }

    const metadata = await response.json();
    const firstImage = metadata.image || null;
    return { ...metadata, image: firstImage };
  } catch (error) {
    
    throw error;
  }
};

const fontFamilies = {
  Bodoni72Smallcaps: "Bodoni72Smallcaps",
};

const guestInputStyle = {
  width: '100%',
  padding: '12px',
  marginBottom: '12px',
  borderRadius: '8px',
  border: '1px solid #ddd',
  fontSize: '16px',
  fontFamily: 'Orpheus Pro, serif',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
  outline: 'none',
  color: '#333',
};

const guestModalButtonBase = {
  flex: 1,
  padding: '13px 24px',
  borderRadius: '40px',
  border: 'none',
  fontSize: '16px',
  fontWeight: '500',
  fontFamily: 'Orpheus Pro, serif',
  minWidth: '120px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
};

const EventCard = ({ onToggleCatalogue, card, onRemove, onSave, onAdd, isOwner, isLastCard, isVisitorView, isPreviewMode, widgetIndex, renderFetchMetadataButton, currentUserData, pageOwner, pageOwnerId, pageTextColor, setCards, featureFlags, isAddingWidget, isRemovingWidget }) => {
  const { currentUser } = useUser();
  const myTasselPlus = useMyTasselPlus();
  const canUseEventImages = myTasselPlus.canUseFeature(MYTASSEL_PLUS_FEATURES.EVENT_IMAGES);
  const { width: viewportWidth } = useWindowDimensions();
  const isNarrowViewport = viewportWidth < 768;
  const cardWidth = '100%';
  const cardMaxWidth = isNarrowViewport ? (selectedWidgetType !== 'TextBox' ? 370 : '100%') : 800;
  
  // Initialize states with existing data
  const [text, setText] = useState(card.content || "");
  const [images, setImages] = useState(card.images || []);
  const [url, setUrl] = useState(card.url || "");
  const [customImageUrl, setCustomImageUrl] = useState(card.customImageUrl || "");
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedWidgetType, setSelectedWidgetType] = useState(typeof card.type === 'string' ? card.type : "");
  const [rsvp, setRSVP] = useState(""); // Initialize empty, will be set by useEffect based on current user
  const rsvpJustSubmitted = useRef(false); // Track when RSVP was just submitted to prevent useEffect from overriding
  
  // Local state to track RSVP counts for immediate UI updates
  const [localRsvpCounts, setLocalRsvpCounts] = useState({
    going: card.going || [],
    maybe: card.maybe || [],
    decline: card.decline || []
  });
  
  const [isLoading, setIsLoading] = useState(false); // New loading state
  const [loadingMessage, setLoadingMessage] = useState(""); // State for loading message
  const [successMessage, setSuccessMessage] = useState(""); // State for success message
  const [errorMessage, setErrorMessage] = useState(""); // State for error message
  const [textInputHeight, setTextInputHeight] = useState(0); // New state for dynamic height
  const [isTextFocused, setIsTextFocused] = useState(false);
  const [targetDate, setTargetDate] = useState(new Date(card.targetDate || Date.now()));
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [onCompleteMessage, setOnCompleteMessage] = useState(card.onCompleteMessage || "The event has started!");
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);
  const [eventId, setEventId] = useState(card.eventId || card.timelineEventId || null);
  const isMounted = useRef(true);
  
  // Event-specific states

  const [isEditingEvent, setIsEditingEvent] = useState(false);
  const [eventDetails, setEventDetails] = useState({
    title: card.eventTitle || card.title || '',
    date: card.eventDate || card.datetime || '',
    endDate: card.eventEndDate || card.endDatetime || '',
    location: card.eventLocation || card.location || '',
    description: card.eventDescription || card.description || '',
    locationCoords: card.locationCoords || null
  });
  
  // Update local RSVP counts when card prop changes (from external updates)
  useEffect(() => {
    if (!rsvpJustSubmitted.current) {
      setLocalRsvpCounts({
        going: card.going || [],
        maybe: card.maybe || [],
        decline: card.decline || []
      });
    }
  }, [card.going, card.maybe, card.decline]);
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');

  // RSVP states - integrated from RSVPWidgets (moved above hook so the hook reads the value correctly)
  const [rsvpEnabled, setRsvpEnabled] = useState(card.rsvpEnabled || false);
  const [reminderSettings, setReminderSettings] = useState(card.reminderSettings || { enabled: false, reminderTimes: [] });

  // Event photos local state (so we re-render immediately after upload)
  const [eventPhotos, setEventPhotos] = useState(card.eventPhotos || []);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');

  // Sync eventPhotos when card prop updates from parent
  useEffect(() => {
    if (card.eventPhotos && JSON.stringify(card.eventPhotos) !== JSON.stringify(eventPhotos)) {
      setEventPhotos(card.eventPhotos);
    }
  }, [card.eventPhotos]);

  // Handler for photo changes from the step wizard — updates state AND persists to Firestore
  const handlePhotosChange = async (updatedPhotos) => {
    setEventPhotos(updatedPhotos);
    if (isPreviewMode) {
      if (setCards) {
        setCards(currentCards => currentCards.map(currentCard => (
          currentCard.id === card.id ? { ...currentCard, eventPhotos: updatedPhotos } : currentCard
        )));
      }
      return;
    }

    try {
      const db = getFirestore();
      const userQuery = query(collection(db, "users"), where("uid", "==", pageOwnerId));
      const qs = await getDocs(userQuery);
      if (!qs.empty) {
        const docSnap = qs.docs[0];
        const data = docSnap.data();
        const cards = data.pages?.eventsPageConfig?.cards || [];
        const photoIdx = cards.findIndex(c => c.id === card.id);
        if (photoIdx !== -1) {
          cards[photoIdx].eventPhotos = updatedPhotos;
          const cleanedCards = cleanForFirestore(cards);
          await updateDoc(doc(db, "users", docSnap.id), { 'pages.eventsPageConfig.cards': cleanedCards });
          if (setCards) setCards([...cleanedCards]);
        }
      }
    } catch (err) {
      console.error('Failed to persist photo changes:', err);
    }
  };

  // Cross-platform photo picker - uses expo-image-picker (works on all platforms)
  const pickEventPhotos = async () => {
    if (uploadingPhotos) {
      console.log('Already uploading photos, ignoring...');
      return;
    }

    if (!myTasselPlus.requireFeature(MYTASSEL_PLUS_FEATURES.EVENT_IMAGES)) {
      return;
    }

    console.log('Photo picker triggered, Platform:', Platform.OS);
    
    // Always show immediate feedback
    setUploadingPhotos(true);
    setUploadProgress('Opening photo picker...');

    // Mobile native app: Use Expo's image picker with action sheet
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      // Request permissions first
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      console.log('Permission status:', status);
      
      if (status !== 'granted') {
        Alert.alert('Permission Required', 'Please allow photo library access to add photos.');
        setUploadingPhotos(false);
        setUploadProgress('');
        return;
      }

      // Show action sheet to choose Camera or Photo Library
      Alert.alert(
        'Add Photos',
        'Choose a source',
        [
          {
            text: 'Camera',
            onPress: async () => {
              console.log('Camera option selected');
              
              // Request camera permission
              const cameraStatus = await ImagePicker.requestCameraPermissionsAsync();
              console.log('Camera permission status:', cameraStatus.status);
              
              if (cameraStatus.status !== 'granted') {
                Alert.alert('Permission Required', 'Please allow camera access to take photos.');
                setUploadingPhotos(false);
                setUploadProgress('');
                return;
              }
              
              setUploadProgress('Opening camera...');
              
              try {
                const result = await ImagePicker.launchCameraAsync({
                  mediaTypes: ImagePicker.MediaTypeOptions.Images,
                  allowsEditing: false,
                  quality: 0.8,
                });
                
                console.log('Camera result:', result);
                
                if (!result.canceled && result.assets && result.assets.length > 0) {
                  console.log('Camera captured', result.assets.length, 'photo(s)');
                  await uploadSelectedPhotos(result.assets);
                } else {
                  console.log('User cancelled camera');
                  setUploadingPhotos(false);
                  setUploadProgress('');
                }
              } catch (error) {
                console.error('Camera exception:', error);
                setUploadingPhotos(false);
                setUploadProgress('');
                Alert.alert('Error', 'Failed to open camera: ' + error.message);
              }
            },
          },
          {
            text: 'Photo Library',
            onPress: async () => {
              console.log('Photo Library option selected');
              setUploadProgress('Opening photo library...');
              
              try {
                const result = await ImagePicker.launchImageLibraryAsync({
                  mediaTypes: ImagePicker.MediaTypeOptions.Images,
                  allowsMultipleSelection: true,
                  quality: 0.8,
                });
                
                console.log('Photo library result:', result);
                
                if (!result.canceled && result.assets && result.assets.length > 0) {
                  console.log('Photo library returned', result.assets.length, 'photo(s)');
                  await uploadSelectedPhotos(result.assets);
                } else {
                  console.log('User cancelled or no photos selected');
                  setUploadingPhotos(false);
                  setUploadProgress('');
                }
              } catch (error) {
                console.error('Photo library exception:', error);
                setUploadingPhotos(false);
                setUploadProgress('');
                Alert.alert('Error', 'Failed to open photo library: ' + error.message);
              }
            },
          },
          {
            text: 'Cancel',
            style: 'cancel',
            onPress: () => {
              console.log('User cancelled photo picker');
              setUploadingPhotos(false);
              setUploadProgress('');
            }
          },
        ],
        { cancelable: true }
      );
    } 
    // Web (including mobile browsers like Safari): Use HTML file input
    else {
      console.log('Using web file input for mobile browser');
      
      try {
        const ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '';
        const isIOSWeb = /iPad|iPhone|iPod/.test(ua) || (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        console.log('Opening web file input', { isIOSWeb });

        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.multiple = true;
        input.value = '';
        
        // Add capture attribute for mobile browsers to show camera/gallery options
        input.setAttribute('capture', 'environment');

        // iOS Safari: input must be attached to DOM when clicked.
        input.style.position = 'fixed';
        input.style.top = '0';
        input.style.left = '0';
        input.style.width = '1px';
        input.style.height = '1px';
        input.style.opacity = '0';
        input.style.pointerEvents = 'none';

        const cleanup = () => {
          try {
            if (input && input.parentNode) input.parentNode.removeChild(input);
          } catch (e) {
            // ignore
          }
        };
        
        input.onchange = async (changeEvent) => {
          const files = Array.from(changeEvent.target.files);
          console.log('Files selected:', files.length);

          cleanup();
          
          if (files.length === 0) {
            console.log('No files selected');
            setUploadingPhotos(false);
            setUploadProgress('');
            return;
          }
          
          // Keep loading state - it's already set
          setUploadProgress(`Preparing ${files.length} photo${files.length > 1 ? 's' : ''}...`);
          
          // Convert web File objects to format compatible with uploadSelectedPhotos
          const assets = files.map(file => ({
            uri: URL.createObjectURL(file),
            type: file.type,
            fileName: file.name,
            fileSize: file.size,
            file: file, // Keep original file for upload
          }));
          
          await uploadSelectedPhotos(assets);
        };
        
        // Handle cancel (when user closes picker without selecting)
        input.oncancel = () => {
          console.log('User cancelled file picker');
          cleanup();
          setUploadingPhotos(false);
          setUploadProgress('');
        };
        
        // Trigger the file picker
        document.body.appendChild(input);
        try {
          input.click();
        } catch (err) {
          console.error('input.click() failed:', err);
          cleanup();
          setUploadingPhotos(false);
          setUploadProgress('');
        }
        
        // Also listen for window focus to detect when picker is closed without selection
        const handleFocus = () => {
          setTimeout(() => {
            // Check if we're still in uploading state but no files were selected
            if (uploadingPhotos && input.files.length === 0) {
              console.log('No files selected after picker closed');
              setUploadingPhotos(false);
              setUploadProgress('');
            }
            cleanup();
          }, 500);
        };
        
        window.addEventListener('focus', handleFocus, { once: true });
        
      } catch (error) {
        console.error('Error opening file picker:', error);
        setUploadingPhotos(false);
        setUploadProgress('');
      }
    }
  };

  // Upload photos to Firebase Storage (works for both mobile and web)
  const uploadSelectedPhotos = async (assets) => {
    console.log('uploadSelectedPhotos called with', assets?.length, 'assets');
    if (!myTasselPlus.requireFeature(MYTASSEL_PLUS_FEATURES.EVENT_IMAGES)) {
      setUploadingPhotos(false);
      setUploadProgress('');
      return;
    }

    if (!assets || assets.length === 0) {
      console.log('No assets to upload');
      setUploadingPhotos(false);
      setUploadProgress('');
      return;
    }
    
    setUploadingPhotos(true);
    setUploadProgress(`Uploading 0 of ${assets.length}...`);
    
    try {
      if (isPreviewMode) {
        const previewPhotos = assets.map(asset => asset.uri).filter(Boolean);
        const newPhotos = [...eventPhotos, ...previewPhotos];
        setEventPhotos(newPhotos);
        if (setCards) {
          setCards(currentCards => currentCards.map(currentCard => (
            currentCard.id === card.id ? { ...currentCard, eventPhotos: newPhotos } : currentCard
          )));
        }
        setUploadProgress('');
        setUploadingPhotos(false);
        setSuccessMessage(`${previewPhotos.length} photo${previewPhotos.length > 1 ? 's' : ''} added in preview. Changes are not saved.`);
        setTimeout(() => setSuccessMessage(''), 3000);
        return;
      }

      console.log('Starting upload process...');
      const storage = getStorage();
      const newPhotos = [...eventPhotos];
      console.log('Current photos:', eventPhotos.length);
      
      for (let i = 0; i < assets.length; i++) {
        console.log(`Processing photo ${i + 1}/${assets.length}`);
        setUploadProgress(`Uploading ${i + 1} of ${assets.length}...`);
        const asset = assets[i];
        console.log('Asset info:', { uri: asset.uri, type: asset.type, fileName: asset.fileName });
        
        // Prepare file for upload
        let fileToUpload;
        if (Platform.OS === 'web') {
          console.log('Using web file object');
          fileToUpload = asset.file; // Use original File object on web
        } else {
          console.log('Fetching blob from URI:', asset.uri);
          // On mobile, fetch the URI as a blob
          const response = await fetch(asset.uri);
          fileToUpload = await response.blob();
          console.log('Blob created, size:', fileToUpload.size);
        }
        
        const fileName = asset.fileName || `photo_${Date.now()}_${i}.jpg`;
        const storagePath = `eventPhotos/${pageOwnerId}/${Date.now()}_${fileName}`;
        console.log('Uploading to storage path:', storagePath);
        const storageRef = ref(storage, storagePath);
        
        await uploadBytes(storageRef, fileToUpload);
        console.log('Upload complete, getting download URL...');
        const downloadUrl = await getDownloadURL(storageRef);
        console.log('Download URL obtained:', downloadUrl);
        newPhotos.push(downloadUrl);
      }
      
      console.log('All photos uploaded, total:', newPhotos.length);
      setEventPhotos([...newPhotos]);
      
      // Update Firestore
      console.log('Updating Firestore...');
      const db = getFirestore();
      const userQuery = query(collection(db, "users"), where("uid", "==", pageOwnerId));
      const querySnapshot = await getDocs(userQuery);
      if (!querySnapshot.empty) {
        const docSnapshot = querySnapshot.docs[0];
        const userData = docSnapshot.data();
        const existingCards = userData.pages?.eventsPageConfig?.cards || [];
        const upPhotoIdx = existingCards.findIndex(c => c.id === card.id);
        if (upPhotoIdx !== -1) {
          existingCards[upPhotoIdx].eventPhotos = newPhotos;
          const cleanedUploadCards = cleanForFirestore(existingCards);
          await updateDoc(doc(db, "users", docSnapshot.id), { 'pages.eventsPageConfig.cards': cleanedUploadCards });
          console.log('Firestore updated successfully');
          if (setCards) setCards([...cleanedUploadCards]);
        } else {
          console.warn('Card not found in existing cards');
        }
      } else {
        console.warn('User document not found');
      }
      
      setUploadProgress('');
      setUploadingPhotos(false);
      const successMsg = `${assets.length} photo${assets.length > 1 ? 's' : ''} added!`;
      setSuccessMessage(successMsg);
      console.log('Upload complete:', successMsg);
      
      // Show alert on mobile for better visibility
      if (Platform.OS === 'ios' || Platform.OS === 'android') {
        Alert.alert('Success', successMsg);
      }
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error uploading event photos:', err);
      console.error('Error stack:', err.stack);
      setUploadProgress('');
      setUploadingPhotos(false);
      const errorMsg = 'Failed to upload: ' + err.message;
      setErrorMessage(errorMsg);
      
      // Show alert on mobile for better visibility
      if (Platform.OS === 'ios' || Platform.OS === 'android') {
        Alert.alert('Upload Failed', err.message || 'Unknown error occurred');
      }
      
      setTimeout(() => setErrorMessage(''), 4000);
    }
  };

  // Event URL state
  const [eventUrl, setEventUrl] = useState(card.eventUrl || '');

  // Widget dropdown open state
  const [showWidgetDropdown, setShowWidgetDropdown] = useState(false);
  useEmitSpotlightEventWhenOpened(showWidgetDropdown, SPOTLIGHT_EVENTS.ANNOUNCEMENT_WIDGET_PICKER_OPENED);

  // Live countdown timer for event display
  const [countdownTime, setCountdownTime] = useState(null);
  useEffect(() => {
    const dateStr = eventDetails.date || card.eventDate || card.datetime;
    if (!dateStr) return;
    const calc = () => {
      const target = new Date(dateStr).getTime();
      const now = Date.now();
      if (isNaN(target) || target <= now) { setCountdownTime(null); return; }
      const diff = target - now;
      setCountdownTime({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        mins: Math.floor((diff / 1000 / 60) % 60),
        secs: Math.floor((diff / 1000) % 60)
      });
    };
    calc();
    const interval = setInterval(calc, 1000);
    return () => clearInterval(interval);
  }, [eventDetails.date, card.eventDate, card.datetime]);

  // Mapbox and event utilities provided by useEventsWidget
  const {
    modalMapContainerRef,
    modalMapRef,
    modalMarkerRef,
    modalGeocoderRef,
    modalMapInitialized,
    viewMapContainerRef,
    searchLocations,
    handleLocationChange,
    selectLocation,
    locationSuggestions,
    showLocationSuggestions,
    setShowLocationSuggestions,
    lastSelectedLocationRef,
    saveEventDetails,
    localTitle,
    localDescription,
    setLocalTitle,
    setLocalDescription,
    debouncedSetTitle,
    debouncedSetDescription,
  } = useEventsWidget({
    eventDetails,
    setEventDetails,
    isEditingEvent,
    pageOwnerId,
    pageOwner,
    widgetIndex,
    widgetId: card.id, // pass widget id so the hook can resolve stored eventId reliably
    rsvpEnabled,
    reminderSettings,
    setReminderSettings,
    eventId,
    setEventId,
    setHasChanges,
    setSuccessMessage,
    setErrorMessage,
    isPreviewMode
  });
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const [activeRSVPTab, setActiveRSVPTab] = useState('Going');
  const [showGuestInfoModal, setShowGuestInfoModal] = useState(false);
  const [guestInfo, setGuestInfo] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [isSubmittingRSVP, setIsSubmittingRSVP] = useState(false);
  const [tempRSVPSelection, setTempRSVPSelection] = useState(null);
  const [userInfoCache, setUserInfoCache] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(false);
  const userListRef = useRef(null);
  const [showBottomShadow, setShowBottomShadow] = useState(false);
  
  
  // Location autocomplete moved to `useEventsWidget` hook

  // Helper function to get user's email (prioritize Firestore email, fallback to Firebase auth email)
  const getUserEmail = () => {
    const firestoreEmail = currentUserData?.email;
    const authEmail = currentUser?.email;
    const email = firestoreEmail || authEmail;
    return email ? email.toLowerCase() : null;
  };

  // Location search moved into `useEventsWidget` hook

  // Location change handler moved into `useEventsWidget` hook

  // Location selection handled by `useEventsWidget` hook

  // Date/time picker helpers
  const formatDateTimeForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      const str = String(dateString).trim();
      // Extract the date portion before any T to avoid timezone shifts
      const datePortion = str.includes('T') ? str.split('T')[0] : str;
      // Parse as local date by appending T12:00 to avoid UTC midnight drift
      const date = new Date(datePortion + 'T12:00:00');
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatTimeForDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      const str = String(dateString).trim();
      // If the string is just a date with no time component, skip
      // Matches "2026-05-15", "May 15, 2026", etc. (no T or time portion)
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return '';
      // Check for explicit time marker (T separator with HH:MM)
      const hasExplicitTime = /T\d{2}:\d{2}/.test(str);
      if (!hasExplicitTime) return '';
      // Extract hours/minutes directly from the string to avoid timezone issues
      const timeMatch = str.match(/T(\d{2}):(\d{2})/);
      if (timeMatch) {
        let hours = parseInt(timeMatch[1], 10);
        const minutes = timeMatch[2];
        const ampm = hours >= 12 ? 'PM' : 'AM';
        if (hours === 0) hours = 12;
        else if (hours > 12) hours -= 12;
        return `${hours}:${minutes} ${ampm}`;
      }
      return '';
    } catch {
      return '';
    }
  };

  // Legacy compat — some callers might still use this
  const formatDateTimeForDisplay = (dateString) => {
    const datePart = formatDateForDisplay(dateString);
    const timePart = formatTimeForDisplay(dateString);
    if (datePart && timePart) return `${datePart} at ${timePart}`;
    return datePart || '';
  };

  const formatTimeOnly = (dateString) => {
    if (!dateString) return '';
    try {
      const str = String(dateString).trim();
      const timeMatch = str.match(/T(\d{2}):(\d{2})/);
      if (timeMatch) {
        let hours = parseInt(timeMatch[1], 10);
        const minutes = timeMatch[2];
        const ampm = hours >= 12 ? 'PM' : 'AM';
        if (hours === 0) hours = 12;
        else if (hours > 12) hours -= 12;
        return `${hours}:${minutes} ${ampm}`;
      }
      return '';
    } catch {
      return '';
    }
  };

  // RSVP Management Functions
  const handleRSVP = async (selection) => {
    if (!currentUserData) {
      setTempRSVPSelection(selection);
      setShowGuestInfoModal(true);
      return;
    }
    
    const userEmail = getUserEmail();
    
    // Prevent RSVP if user has no email and no name
    if (!userEmail && !currentUserData.firstName && !currentUserData.lastName) {
      alert('You must have an email or name to RSVP. Please update your profile.');
      return;
    }
    
    // Additional check: if no email at all, prevent RSVP
    if (!userEmail) {
      alert('Unable to determine user email. Please log in again and try RSVP.');
      return;
    }
    
    // If the user clicks the same RSVP, unselect it
    if (rsvp === selection) {
      try {
        await submitRSVP("", userEmail, currentUserData.firstName, currentUserData.lastName);
      } catch (error) {
        console.error('Failed to update RSVP:', error);
        alert("Failed to update RSVP. Please try again.");
      }
      return;
    }
    
    try {
      await submitRSVP(selection, userEmail, currentUserData.firstName, currentUserData.lastName);
    } catch (error) {
      console.error('Failed to update RSVP:', error);
      alert("Failed to update RSVP. Please try again.");
    }
  };

  const submitRSVP = async (selection, email, firstName, lastName, phone = null) => {
    // Validate email parameter
    if (!email) {
      throw new Error("Email is required for RSVP");
    }
    
    // Check if user is logged in - use getUserEmail to get the correct user email
    const userEmail = getUserEmail();
    const isLoggedInUser = currentUserData && userEmail === email;
    
    const db = getFirestore();
    const rsvpOwnerEmail = pageOwner?.email?.toLowerCase();
    if (!rsvpOwnerEmail) {
      console.error("saveRSVP: No pageOwner email available");
      return;
    }
    const cardRef = doc(db, "users", rsvpOwnerEmail);

    try {
      // For guest users, sign in anonymously to get write permissions
      if (!isLoggedInUser) {
        const auth = getAuth();
        // Only sign in if not already authenticated (to prevent unnecessary auth state changes)
        if (!auth.currentUser) {
          await signInAnonymously(auth);
        }
      }

      // Get the current card data
      const cardSnap = await getDoc(cardRef);
      if (!cardSnap.exists()) {
        console.error("User document not found for:", rsvpOwnerEmail);
        throw new Error("User document not found");
      }

      const cardData = cardSnap.data();
      const widgets = cardData.pages?.eventsPageConfig?.cards || [];
      
      // Find widget by ID (not positional index which can be stale)
      const rsvpWidgetIdx = widgets.findIndex(w => w.id === card.id);
      if (rsvpWidgetIdx === -1) {
        console.error("Widget not found by ID:", card.id);
        throw new Error("Widget not found");
      }
      
      const currentWidget = widgets[rsvpWidgetIdx];
      
      // Initialize RSVP arrays if they don't exist
      const going = currentWidget.going || [];
      const maybe = currentWidget.maybe || [];
      const decline = currentWidget.decline || [];

      let updatedGoing = [...going];
      let updatedMaybe = [...maybe];
      let updatedDecline = [...decline];

      // Remove user from all lists first
      if (isLoggedInUser) {
        updatedGoing = updatedGoing.filter(userEmail => userEmail !== email);
        updatedMaybe = updatedMaybe.filter(userEmail => userEmail !== email);
        updatedDecline = updatedDecline.filter(userEmail => userEmail !== email);
      } else {
        // For guest users, remove by email
        updatedGoing = updatedGoing.filter(entry => typeof entry === 'string' ? entry !== email : entry.email !== email);
        updatedMaybe = updatedMaybe.filter(entry => typeof entry === 'string' ? entry !== email : entry.email !== email);
        updatedDecline = updatedDecline.filter(entry => typeof entry === 'string' ? entry !== email : entry.email !== email);
      }

      // Normalize phone: strip formatting, auto-add +1 for 10-digit US numbers
      const normalizePhone = (raw) => {
        if (!raw) return null;
        const digits = raw.replace(/\D/g, '');
        if (digits.length === 10) return `+1${digits}`;
        if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
        if (digits.length >= 7) return `+${digits}`;
        return null;
      };
      const normalizedPhone = normalizePhone(phone);

      // Only add user to a list if selection is not empty
      const guestEntry = (extra = {}) => ({
        firstName,
        lastName,
        email,
        ...(normalizedPhone ? { phone: normalizedPhone } : {}),
        timestamp: new Date().toISOString(),
        ...extra,
      });

      if (selection === "Going") {
        if (isLoggedInUser) {
          updatedGoing.push(email);
        } else {
          updatedGoing.push(guestEntry());
        }
      } else if (selection === "Maybe") {
        if (isLoggedInUser) {
          updatedMaybe.push(email);
        } else {
          updatedMaybe.push(guestEntry());
        }
      } else if (selection === "Decline") {
        if (isLoggedInUser) {
          updatedDecline.push(email);
        } else {
          updatedDecline.push(guestEntry());
        }
      }

      // Update the current widget (using ID-based index)
      widgets[rsvpWidgetIdx] = {
        ...currentWidget,
        going: updatedGoing,
        maybe: updatedMaybe,
        decline: updatedDecline,
      };

      // Deep-clean to prevent Firestore undefined rejections
      const cleanedRsvpWidgets = cleanForFirestore(widgets);

      if (isPreviewMode) {
        rsvpJustSubmitted.current = true;
        setRSVP(selection);
        setLocalRsvpCounts({
          going: updatedGoing,
          maybe: updatedMaybe,
          decline: updatedDecline
        });

        if (typeof window !== 'undefined' && typeof window.updateParentCards === 'function') {
          const updatedCard = { ...card, going: updatedGoing, maybe: updatedMaybe, decline: updatedDecline };
          window.updateParentCards(widgetIndex, updatedCard);
        }

        setTimeout(() => {
          rsvpJustSubmitted.current = false;
        }, 1000);
        return;
      }

      // Update Firestore document with cleaned data
      await updateDoc(cardRef, {
        "pages.eventsPageConfig.cards": cleanedRsvpWidgets
      });

      // Mark that we just submitted RSVP to prevent useEffect from overriding it
      rsvpJustSubmitted.current = true;
      
      // Update local state
      setRSVP(selection);
      
      // Update local RSVP counts immediately for UI responsiveness
      setLocalRsvpCounts({
        going: updatedGoing,
        maybe: updatedMaybe,
        decline: updatedDecline
      });

      // Sync updated RSVP arrays back to the parent cards state so that any
      // subsequent re-render of Events.js passes fresh props — preventing the
      // useEffect guard from resetting localRsvpCounts to stale data.
      if (typeof window !== 'undefined' && typeof window.updateParentCards === 'function') {
        const updatedCard = { ...card, going: updatedGoing, maybe: updatedMaybe, decline: updatedDecline };
        window.updateParentCards(widgetIndex, updatedCard);
      }

      // For guest users, store their email in sessionStorage so we can highlight their selection
      if (!isLoggedInUser && selection) {
        sessionStorage.setItem('guestRSVPEmail', email);
      }
      
      // Reset the flag after a short delay to allow useEffect to run normally again
      setTimeout(() => {
        rsvpJustSubmitted.current = false;
      }, 1000);

      // Auto-resubscribe user to reminder emails if they RSVP Going/Maybe
      if (email && (selection === 'Going' || selection === 'Maybe')) {
        try {
          const resubscribeResult = await handleAutoResubscribe(
            email,
            pageOwner.email,
            selection,
            true // isClient = true
          );
          if (resubscribeResult.success) {
            console.log('✅ User auto-resubscribed to reminder emails via Event Card RSVP');
          }
        } catch (error) {
          console.error('⚠️ Failed to auto-resubscribe user:', error);
          // Don't show error to user - this is a background operation
        }
      }

      // Trigger reminder rescheduling for this specific widget (useful when RSVP lists change)
      try {
        const reminderIntegration = (await import('../services/ReminderIntegration.js')).default;
        reminderIntegration && reminderIntegration.scheduleRemindersOnRSVPUpdate && reminderIntegration.scheduleRemindersOnRSVPUpdate(pageOwner.email.toLowerCase(), widgets[rsvpWidgetIdx]);
      } catch (e) {
        console.error('Failed to trigger reminder reschedule after RSVP change:', e);
      }
      
      // Close modal if it was open
      setShowGuestInfoModal(false);
    } catch (error) {
      console.error("Failed to submit RSVP:", error);
      alert("Failed to update RSVP. Please try again.");
      throw error;
    }
  };

  const handleGuestInfoSubmit = async () => {
    if (!guestInfo.firstName || !guestInfo.lastName || !guestInfo.email) {
      alert("Please fill in all fields");
      return;
    }

    setIsSubmittingRSVP(true);
    try {
      await submitRSVP(
        tempRSVPSelection,
        guestInfo.email,
        guestInfo.firstName,
        guestInfo.lastName,
        guestInfo.phone || null
      );
      
      // Store guest email so it persists across the session
      sessionStorage.setItem('guestRSVPEmail', guestInfo.email);
      
      // Clear the temp selection
      setTempRSVPSelection(null);
      
      // Don't clear guestInfo immediately - keep it for future RSVPs in this session
      // This way if they want to change their RSVP, the form is pre-filled
    } catch (error) {
      console.error('Failed to submit guest RSVP:', error);
      alert("Failed to update RSVP. Please try again.");
    } finally {
      setIsSubmittingRSVP(false);
    }
  };

  const getRSVPList = (type) => {
    const arr = localRsvpCounts[type]?.length ? localRsvpCounts[type] : [];
    return arr.map(entry => typeof entry === 'string' ? { email: entry } : entry);
  };

  const getUserDisplayInfo = (user) => {
    if (user.firstName) {
      return user;
    }
    if (user.email && userInfoCache[user.email]) {
      return userInfoCache[user.email];
    }
    return user;
  };

  const hasAnyRSVP = (localRsvpCounts.going && localRsvpCounts.going.length) || (localRsvpCounts.maybe && localRsvpCounts.maybe.length) || (localRsvpCounts.decline && localRsvpCounts.decline.length);

  // Clear guest email from sessionStorage when a real user logs in
  useEffect(() => {
    if (currentUserData || currentUser) {
      sessionStorage.removeItem('guestRSVPEmail');
    }
  }, [currentUserData, currentUser]);

  // Effect to update state when card data changes
  // Use a more stable approach to prevent excessive re-renders
  useEffect(() => {
    if (card) {
      setText(prevText => {
        const newText = card.content || "";
        return prevText !== newText ? newText : prevText;
      });
      
      setImages(prevImages => {
        const newImages = card.images || [];
        return JSON.stringify(prevImages) !== JSON.stringify(newImages) ? newImages : prevImages;
      });
      
      setUrl(prevUrl => {
        const newUrl = card.url || "";
        return prevUrl !== newUrl ? newUrl : prevUrl;
      });
      
      setCustomImageUrl(prevCustomImageUrl => {
        const newCustomImageUrl = card.customImageUrl || "";
        return prevCustomImageUrl !== newCustomImageUrl ? newCustomImageUrl : prevCustomImageUrl;
      });
      
      // Only update selectedWidgetType if the card prop has a valid string type
      if (card.type && typeof card.type === 'string') {
        setSelectedWidgetType(prevType => {
          return prevType !== card.type ? card.type : prevType;
        });
      }
      
      setTargetDate(prevDate => {
        const newDate = new Date(card.targetDate || Date.now());
        return prevDate.getTime() !== newDate.getTime() ? newDate : prevDate;
      });
      
      setOnCompleteMessage(prevMessage => {
        const newMessage = card.onCompleteMessage || "The event has started!";
        return prevMessage !== newMessage ? newMessage : prevMessage;
      });
      
      setEventId(prevEventId => {
        const newEventId = card.eventId || card.timelineEventId || null;
        return prevEventId !== newEventId ? newEventId : prevEventId;
      });
      
      // Update Event-specific states with comparison
      const loadedDate = card.eventDate || card.datetime || '';
      const loadedEndDate = card.eventEndDate || card.endDatetime || '';
      const newEventDetails = {
        title: card.eventTitle || card.title || '',
        date: loadedDate,
        endDate: loadedEndDate,
        location: card.eventLocation || card.location || '',
        description: card.eventDescription || card.description || '',
        // Support both keys - some parts of the app used `eventLocationCoords` and others `locationCoords`
        locationCoords: card.locationCoords || card.eventLocationCoords || null
      };
      
      
      if (!isEditingEvent) {
        setEventDetails(prevEventDetails => {
          // Only update if there are actual changes
          const hasChanges = Object.keys(newEventDetails).some(
            key => JSON.stringify(prevEventDetails[key]) !== JSON.stringify(newEventDetails[key])
          );
          if (hasChanges) console.log('[EventCard] updating eventDetails from', prevEventDetails, 'to', newEventDetails, 'card.id=', card.id);
          return hasChanges ? newEventDetails : prevEventDetails;
        });
      } else {
      //  console.log('[EventCard] skipping eventDetails update because editor is open (isEditingEvent=true) for card.id=', card.id);
      }
      
      setRsvpEnabled(prevRsvpEnabled => {
        const newRsvpEnabled = card.rsvpEnabled || false;
        return prevRsvpEnabled !== newRsvpEnabled ? newRsvpEnabled : prevRsvpEnabled;
      });
      
      setEventUrl(prevEventUrl => {
        const newEventUrl = card.eventUrl || '';
        return prevEventUrl !== newEventUrl ? newEventUrl : prevEventUrl;
      });

      // Update RSVP state based on current user or guest email
      // Skip this update if we just submitted an RSVP to prevent overriding the submitted state
      if (rsvpJustSubmitted.current) {
        return;
      }
      
      const userEmail = getUserEmail();
      const guestEmail = sessionStorage.getItem('guestRSVPEmail');
      const emailToCheck = userEmail || guestEmail;
      
      if (emailToCheck) {
        // Check if user/guest email is in going array
        const isInGoing = card.going?.some(entry => 
          typeof entry === 'string' ? entry === emailToCheck : entry.email === emailToCheck
        );
        const isInMaybe = card.maybe?.some(entry => 
          typeof entry === 'string' ? entry === emailToCheck : entry.email === emailToCheck
        );
        const isInDecline = card.decline?.some(entry => 
          typeof entry === 'string' ? entry === emailToCheck : entry.email === emailToCheck
        );
        
        const newRSVP = isInGoing ? "Going" : isInMaybe ? "Maybe" : isInDecline ? "Decline" : "";
        setRSVP(prevRSVP => prevRSVP !== newRSVP ? newRSVP : prevRSVP);
      } else {
        // Reset RSVP state when user is logged out and no guest email
        setRSVP(prevRSVP => prevRSVP !== "" ? "" : prevRSVP);
      }
    }
  }, [
    // Only listen to specific properties that should trigger this sync, not the entire card object
    card?.content, 
    card?.images, 
    card?.url, 
    card?.customImageUrl,
    card?.type,
    card?.targetDate,
    card?.onCompleteMessage,
    card?.eventId,
    card?.timelineEventId,
    card?.eventTitle,
    card?.title,
    card?.eventDate,
    card?.datetime,
    card?.eventEndDate,
    card?.endDatetime,
    card?.eventLocation,
    card?.location,
    card?.eventDescription,
    card?.description,
    card?.locationCoords,
    card?.eventLocationCoords,
    card?.rsvpEnabled,
    card?.eventUrl,
    card?.going,
    card?.maybe,
    card?.decline,
    currentUserData,
    currentUser,
    pageOwnerId
  ]);



  // Parse date when modal opens
  useEffect(() => {
    if (isEditingEvent && eventDetails.date) {
      try {
        const date = new Date(eventDetails.date);
        if (!isNaN(date.getTime())) {
          setEventDate(date.toISOString().split('T')[0]);
          setEventTime(date.toTimeString().split(' ')[0].substring(0, 5));
        } else {
          setEventDate('');
          setEventTime('');
        }
      } catch (e) {
        setEventDate('');
        setEventTime('');
      }
    }
  }, [isEditingEvent, eventDetails.date]);

  // Mapbox initialization moved to `useEventsWidget` hook

  // Saving event details moved into `useEventsWidget` hook

  // Cleanup function for removing Event widget and associated data
  const handleRemoveEventWidget = async () => {
    console.log('🗑️ [handleRemoveEventWidget] type:', selectedWidgetType, 'card.id:', card.id);
    if (isPreviewMode) {
      onRemove();
      setSuccessMessage('Widget removed in preview. Changes are not saved.');
      setTimeout(() => setSuccessMessage(""), 2000);
      return;
    }

    if (!pageOwnerId || selectedWidgetType !== 'Event') {
      // For non-Event widgets, just use the regular remove function
      console.log('🗑️ [handleRemoveEventWidget] Non-Event widget — calling onRemove() directly');
      onRemove();
      return;
    }
    
    try {
      const db = getFirestore();
      const q = query(collection(db, "users"), where("uid", "==", pageOwnerId));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const docSnapshot = querySnapshot.docs[0];
        const userDocRef = doc(db, "users", docSnapshot.id);
        const userData = docSnapshot.data();
        const existingCards = userData.pages?.eventsPageConfig?.cards || [];
        
        // Remove the Event widget by ID (not positional index which can be stale)
        const removeIdx = existingCards.findIndex(c => c.id === card.id);
        if (removeIdx !== -1) {
          existingCards.splice(removeIdx, 1);
        }
        
        const cleanedRemoveCards = cleanForFirestore(existingCards);
        await updateDoc(userDocRef, {
          'pages.eventsPageConfig.cards': cleanedRemoveCards
        });
        
        setSuccessMessage('Event and all associated data removed successfully!');
        setTimeout(() => setSuccessMessage(""), 2000);
      }
    } catch (error) {
      console.error('Failed to remove event widget:', error);
      setErrorMessage('Failed to remove event. Please try again.');
      setTimeout(() => setErrorMessage(""), 2000);
    }
    
    // Call the original remove function
    onRemove();
  };


  const handleTextChange = (newText) => {
    setText(newText);
    setHasChanges(true);
  };

  const handleWidgetTypeChange = async (type) => {
    console.log('🔧 [handleWidgetTypeChange] Changing to:', type, 'from:', selectedWidgetType, 'card.id:', card.id);
    setSelectedWidgetType(type);
    setHasChanges(true);

    // If the owner selects Event, auto-save the widget type and open the editor
    if (type === 'Event' && isOwner) {
      try {
        // pass the selected type explicitly to avoid stale state inside saveCard
        // isAutoSave=true: silently skip the write if event fields are still blank
        await saveCard(type, true);
      } catch (e) {
        // ignore save errors here — user can still click Save Changes
      }

      // If creating a fresh Event 
      if (!card.eventTitle && !card.eventId && !card.timelineEventId) {
        setEventDetails({ title: '', date: '', endDate: '', location: '', description: '', locationCoords: null });
        setEventId(null);
        setRsvpEnabled(false);
        setReminderSettings && setReminderSettings({ enabled: false, reminderTimes: [] });
        setHasChanges(true);
      }

      setIsEditingEvent(true);
    }
  };

  const handleUrlChange = (newUrl) => {
    setUrl(newUrl);
    setHasChanges(true);
  };

  const handleFetchMetadata = async () => {
    setIsLoading(true);
    setLoadingMessage("Fetching metadata...");
    try {
      const metadata = await fetchMetadata(url);
      card.metadata = metadata;
      setCustomImageUrl(metadata.image || "https://via.placeholder.com/600");
      setHasChanges(true);
      setLoadingMessage("");
      setSuccessMessage("Metadata fetched successfully!");
      alert("URL retrieved successfully!");
      setTimeout(() => setSuccessMessage(""), 2000);
    } catch (error) {
      
      setLoadingMessage("");
      setErrorMessage("Failed to fetch metadata. Please try again.");
      alert("Failed to retrieve URL. Please try again.");
      setTimeout(() => setErrorMessage(""), 2000);
    } finally {
      setIsLoading(false);
    }


  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const limitFeature = selectedWidgetType === "PictureFrame"
        ? MYTASSEL_PLUS_FEATURES.POLAROID_IMAGES
        : selectedWidgetType === "ImageGallery"
          ? MYTASSEL_PLUS_FEATURES.SLIDESHOW_IMAGES
          : null;

      if (limitFeature && !myTasselPlus.requireItemCapacity(limitFeature, images.length, 1)) {
        event.target.value = '';
        return;
      }

      if (isPreviewMode) {
        const previewUrl = typeof URL !== 'undefined' ? URL.createObjectURL(file) : null;
        if (previewUrl) {
          setCustomImageUrl(previewUrl);
          setImages((currentImages) => [...currentImages, previewUrl]);
        }
        setHasChanges(true);
        setSuccessMessage("Image added in preview. Changes are not saved.");
        setTimeout(() => setSuccessMessage(""), 2000);
        return;
      }

      setIsLoading(true);
      setLoadingMessage("Uploading image...");
      const storagePath = `widgetsImages/${file.name}-${Date.now()}`;
      const storageRef = ref(getStorage(), storagePath);

      try {
        const uploadResult = await uploadBytes(storageRef, file);
        const publicUrl = await getDownloadURL(uploadResult.ref);

        setCustomImageUrl(publicUrl); // Display the selected cover photo immediately
        setImages((currentImages) => [...currentImages, publicUrl]);
        setHasChanges(true); // Mark that there are unsaved changes
        setLoadingMessage(""); // Clear loading message
        setSuccessMessage("Image uploaded successfully!");
        setTimeout(() => setSuccessMessage(""), 2000); // Clear success message after 2 seconds
      } catch (error) {
        
        setLoadingMessage(""); // Clear loading message
        setErrorMessage("Failed to upload image. Please try again.");
        setTimeout(() => setErrorMessage(""), 2000); // Clear error message after 2 seconds
      } finally {
        setIsLoading(false);
      }
    } else {
      
    }
  };

  const handleRemoveImage = (index) => {
    setImages((currentImages) => {
      const updatedImages = [...currentImages];
      updatedImages.splice(index, 1);
      return updatedImages;
    });
    setHasChanges(true);
  };

  const handleDateChange = (event, selectedDate) => {
    const currentDate = selectedDate || targetDate;
    setTargetDate(currentDate);
    setHasChanges(true);
  };

  const handleCompleteMessageChange = (text) => {
    setOnCompleteMessage(text);
    setHasChanges(true);
  };

  // Safely serialize data for Firestore.
  // Manual recursive deep-copy that:
  //  - converts undefined -> null
  //  - strips functions, symbols, DOM nodes, React elements
  //  - catches throwing getters (React Navigation context)
  //  - flattens nested arrays (Firestore rejects them)
  //  - detects circular references via WeakSet
  const cleanForFirestore = (data) => {
    if (data === undefined || data === null) return null;
    const seen = new WeakSet();
    const clean = (val, depth) => {
      if (depth > 30) return null;
      if (val === undefined) return null;
      if (val === null) return null;
      if (typeof val === 'function' || typeof val === 'symbol') return null;
      if (typeof val !== 'object') return val; // primitives pass through
      // Skip non-plain objects
      if (typeof HTMLElement !== 'undefined' && val instanceof HTMLElement) return null;
      if (val.$$typeof) return null; // React element
      if (typeof val.then === 'function') return null; // Promise
      // Circular ref check
      if (seen.has(val)) return null;
      seen.add(val);
      if (Array.isArray(val)) {
        const result = [];
        for (let i = 0; i < val.length; i++) {
          const item = clean(val[i], depth + 1);
          // Firestore: no nested arrays — flatten any inner arrays
          if (Array.isArray(item)) {
            for (let j = 0; j < item.length; j++) {
              if (item[j] !== null && item[j] !== undefined) result.push(item[j]);
            }
          } else if (item !== null && item !== undefined) {
            result.push(item);
          }
        }
        return result;
      }
      // Plain object
      const out = {};
      let keys;
      try { keys = Object.keys(val); } catch (_) { return null; }
      for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        // Firestore rejects field names starting and ending with "__"
        if (k.length > 4 && k.startsWith('__') && k.endsWith('__')) continue;
        try {
          const v = val[k];
          if (typeof v === 'function' || typeof v === 'symbol') continue;
          if (v && v.$$typeof) continue;
          const c = clean(v, depth + 1);
          if (c !== undefined) out[k] = c;
        } catch (_) { /* skip throwing getters */ }
      }
      return out;
    };
    return clean(data, 0);
  };

  const saveCard = async (typeOverride = null, isAutoSave = false) => {
    // Guard: if typeOverride is not a string (e.g. a click event was passed), discard it
    if (typeOverride && typeof typeOverride !== 'string') {
      console.warn('🔧 [saveCard] typeOverride is not a string — ignoring:', typeof typeOverride);
      typeOverride = null;
    }
    const ownerEmail = pageOwner?.email?.toLowerCase();
    console.log('🔧 [saveCard] START — card.id:', card.id, 'typeOverride:', typeOverride, 'selectedWidgetType:', selectedWidgetType, 'ownerEmail:', ownerEmail);
    if (!ownerEmail) {
      console.error("🔧 [saveCard] ABORT — No pageOwner email available.");
      setErrorMessage("Unable to save: missing page owner information.");
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    const db = getFirestore();
    const cardRef = doc(db, "users", ownerEmail);

    try {
      const cardSnap = await getDoc(cardRef);
      if (!cardSnap.exists()) {
        console.error("🔧 [saveCard] ABORT — User doc not found for:", ownerEmail);
        setErrorMessage("Unable to save: user profile not found.");
        setTimeout(() => setErrorMessage(""), 3000);
        return;
      }

      const cardData = cardSnap.data();
      const widgets = cardData.pages?.eventsPageConfig?.cards || [];
      console.log('🔧 [saveCard] Firestore has', widgets.length, 'widgets:', widgets.map(w => ({ id: w.id, type: w.type })));
      
      const typeToSave = (typeof typeOverride === 'string' && typeOverride) ? typeOverride : (typeof selectedWidgetType === 'string' ? selectedWidgetType : '');
      const cardId = card.id;

      if (!typeToSave) {
        console.error('🔧 [saveCard] ABORT — No valid widget type to save');
        setErrorMessage("Unable to save: no widget type selected.");
        setTimeout(() => setErrorMessage(""), 3000);
        return;
      }

      // Find widget by ID in Firestore array (not by positional index — index can be stale after add/delete)
      let targetIdx = widgets.findIndex(w => w.id === cardId);
      console.log('🔧 [saveCard] Looking for card ID:', cardId, '→ found at Firestore index:', targetIdx, '(widgetIndex prop:', widgetIndex, ')');
      if (targetIdx === -1) {
        // Fallback to positional index if ID not found (new widget that hasn't been saved yet)
        console.log('🔧 [saveCard] ID not found in Firestore — falling back to widgetIndex:', widgetIndex);
        targetIdx = widgetIndex;
      }

      // Use the Firestore-loaded widget as the base (already clean serializable data).
      // Do NOT use the `card` prop as the base — it contains React/Navigation context objects.
      const existingWidget = widgets[targetIdx] || {};
      console.log('🔧 [saveCard] existingWidget at index', targetIdx, ':', existingWidget.id, 'type:', existingWidget.type);

      // Build a fresh widgetData object with ONLY plain serializable fields
      const widgetData = {
        id: cardId,
        type: typeToSave,
        content: text || '',
        position: targetIdx,
        metadata: existingWidget.metadata || {},
        events: existingWidget.events || [],
        eventPhotos: existingWidget.eventPhotos || [],
      };
      console.log('🔧 [saveCard] Built widgetData — id:', widgetData.id, 'type:', widgetData.type, 'position:', widgetData.position);

      // Add widget-specific fields based on type
      switch (typeToSave) {
        case "Countdown":
          widgetData.targetDate = targetDate.toISOString();
          widgetData.onCompleteMessage = onCompleteMessage;
          widgetData.eventId = eventId;
          break;
        case "Event":
          widgetData.eventTitle = eventDetails.title || existingWidget.eventTitle || existingWidget.title || '';
          widgetData.eventDate = eventDetails.date || existingWidget.eventDate || existingWidget.datetime || '';
          widgetData.eventEndDate = eventDetails.endDate || existingWidget.eventEndDate || existingWidget.endDatetime || null;
          widgetData.eventLocation = eventDetails.location || existingWidget.eventLocation || existingWidget.location || '';
          widgetData.eventLocationCoords = eventDetails.locationCoords || existingWidget.eventLocationCoords || existingWidget.locationCoords || null;
          widgetData.locationCoords = eventDetails.locationCoords || existingWidget.locationCoords || existingWidget.eventLocationCoords || null;
          widgetData.eventDescription = eventDetails.description || existingWidget.eventDescription || existingWidget.description || '';
          widgetData.eventUrl = eventUrl || existingWidget.eventUrl || existingWidget.url || '';
          widgetData.eventId = eventId || existingWidget.eventId || existingWidget.timelineEventId || null;
          // GUARD: rsvpEnabled and reminderSettings must fall back to Firestore values.
          // These React states default to false/{} on mount, so without a fallback a
          // saveCard() call from handleWidgetTypeChange (or any stale-state trigger)
          // would silently reset a previously-configured RSVP setup to disabled.
          widgetData.rsvpEnabled = typeof rsvpEnabled === 'boolean' ? rsvpEnabled : (existingWidget.rsvpEnabled || false);
          widgetData.reminderSettings = (reminderSettings && (reminderSettings.enabled || (reminderSettings.reminderTimes && reminderSettings.reminderTimes.length > 0)))
            ? reminderSettings
            : (existingWidget.reminderSettings || { enabled: false, reminderTimes: [] });
          widgetData.going = existingWidget.going || [];
          widgetData.maybe = existingWidget.maybe || [];
          widgetData.decline = existingWidget.decline || [];
          break;
        case "RSVP":
          widgetData.going = existingWidget.going || [];
          widgetData.maybe = existingWidget.maybe || [];
          widgetData.decline = existingWidget.decline || [];
          widgetData.rsvp = rsvp;
          break;
        case "ImageGallery":
        case "PictureFrame":
          widgetData.images = images || [];
          break;
        case "TextBox":
          // content already set above
          break;
        case "URLPreview":
          widgetData.url = url || '';
          widgetData.customImageUrl = customImageUrl || '';
          break;
        case "Poll":
          widgetData.question = existingWidget.question || "";
          widgetData.answers = existingWidget.answers || [];
          widgetData.votes = existingWidget.votes || [];
          break;
      }

      // Replace the widget at the found index
      // PRE-WRITE ABSOLUTE GUARD: never commit a blank Event card to Firestore.
      // This blocks both manual saves and the type-change auto-save from
      // handleWidgetTypeChange (isAutoSave=true) when fields are still empty.
      if (typeToSave === 'Event') {
        const effectiveTitle = String(widgetData.eventTitle || '').trim();
        const effectiveDate  = String(widgetData.eventDate  || '').trim();
        if (!effectiveTitle || !effectiveDate) {
          if (!isAutoSave) {
            setErrorMessage('Event title and date are required before saving.');
            setTimeout(() => setErrorMessage(''), 4000);
          }
          console.warn('🔧 [saveCard] BLOCKED — blank Event card (title:', effectiveTitle || 'empty', ', date:', effectiveDate || 'empty', '). isAutoSave:', isAutoSave);
          return; // Block the Firestore write
        }
      }

      if (typeToSave === 'TextBox') {
        const effectiveContent = String(widgetData.content || '').trim();
        if (!effectiveContent) {
          if (!isAutoSave) {
            setErrorMessage('Add some text before saving the Textbox widget.');
            setTimeout(() => setErrorMessage(''), 4000);
          }
          console.warn('🔧 [saveCard] BLOCKED — empty TextBox widget.');
          return; // Block the Firestore write
        }
      }

      if (targetIdx >= 0 && targetIdx < widgets.length) {
        // WRITE GUARD: for Event widgets, block empty data overwriting valid data
        if (typeToSave === 'Event' && wouldOverwriteValidCard(widgets[targetIdx], widgetData)) {
          const validation = validateEventCard(widgetData);
          console.warn('🔧 [saveCard] BLOCKED — empty data would overwrite valid Event card:', validation.errors);
          setErrorMessage('Cannot save: event data appears incomplete.');
          setTimeout(() => setErrorMessage(''), 3000);
          return;
        }
        widgets[targetIdx] = typeToSave === 'Event'
          ? safeEventCardMerge(widgets[targetIdx], widgetData)
          : widgetData;
        console.log('🔧 [saveCard] Replaced widget at index', targetIdx);
      } else {
        // Widget not found — append it
        widgets.push(widgetData);
        console.log('🔧 [saveCard] Appended widget (was out of bounds), new length:', widgets.length);
      }

      // Clean entire array to guarantee Firestore compatibility
      const cleanedWidgets = cleanForFirestore(widgets);
      console.log('🔧 [saveCard] After cleanForFirestore:', cleanedWidgets.length, 'widgets:', cleanedWidgets.map(w => ({ id: w.id, type: w.type })));

      // Update Firestore with the cleaned widget data
      if (isPreviewMode) {
        const savedIdx = cleanedWidgets.findIndex(w => w.id === cardId);
        if (typeof window !== 'undefined' && window.updateParentCards && savedIdx !== -1) {
          try {
            window.updateParentCards(savedIdx, cleanedWidgets[savedIdx]);
          } catch (e) {
            console.warn('🔧 [saveCard] Failed to notify parent during preview:', e);
          }
        }
        setHasChanges(false);
        setSuccessMessage("Preview updated. Changes are not saved.");
        setTimeout(() => setSuccessMessage(""), 2000);
        console.log('🔧 [saveCard] PREVIEW ONLY — skipped Firestore update');
        return;
      }

      await updateDoc(cardRef, {
        "pages.eventsPageConfig.cards": cleanedWidgets
      });
      console.log('🔧 [saveCard] ✅ Firestore updateDoc succeeded');

      // Audit log — fire-and-forget, never awaited in the critical path
      const isNewCard = targetIdx < 0 || targetIdx >= widgets.length - 1 && widgets.length > 0 && widgets[widgets.length - 1]?.id === cardId && !existingWidget?.eventTitle && !existingWidget?.type;
      logWidgetEvent(
        ownerEmail,
        cardId,
        typeToSave,
        isNewCard ? 'widget_created' : 'widget_updated',
        {
          source: 'user',
          caller: 'EventCard.saveCard',
          before: Object.keys(existingWidget).length > 0 ? existingWidget : null,
          after: cleanedWidgets.find(w => w.id === cardId) || null,
        }
      );

      // Find the saved widget in cleaned array for parent notification
      const savedIdx = cleanedWidgets.findIndex(w => w.id === cardId);
      console.log('🔧 [saveCard] savedIdx for parent notify:', savedIdx);

      // Notify parent page to update its local cards state for immediate UI feedback
      if (typeof window !== 'undefined' && window.updateParentCards && savedIdx !== -1) {
        try {
          console.log('🔧 [saveCard] Calling updateParentCards with index:', savedIdx, 'widget:', cleanedWidgets[savedIdx]?.id, 'type:', cleanedWidgets[savedIdx]?.type);
          window.updateParentCards(savedIdx, cleanedWidgets[savedIdx]);
        } catch (e) {
          console.warn('🔧 [saveCard] Failed to notify parent:', e);
        }
      } else {
        console.warn('🔧 [saveCard] Skipping updateParentCards — savedIdx:', savedIdx, 'updateParentCards exists:', !!window.updateParentCards);
      }

      setHasChanges(false);
      setSuccessMessage("Changes saved successfully!");
      setTimeout(() => setSuccessMessage(""), 2000);
      console.log('🔧 [saveCard] DONE ✅');
    } catch (error) {
      console.error("🔧 [saveCard] FAILED ❌:", error);
      setErrorMessage("Failed to save changes. Please try again.");
      setTimeout(() => setErrorMessage(""), 2000);
    }
  };

  const handleContentSizeChange = (contentWidth, contentHeight) => {
    const nextHeight = Math.ceil(Number(contentHeight) || 0);
    setTextInputHeight(previousHeight => (
      Math.abs(previousHeight - nextHeight) <= 1 ? previousHeight : nextHeight
    ));
  };

  const renderWidget = () => {
    const effectiveIsOwner = isOwner && !isVisitorView; 
    switch (selectedWidgetType) {
      case "TextBox":
        // Never show an empty textbox to visitors
        if (!effectiveIsOwner && !String(text || '').trim()) return null;
        const textBoxMinHeight = isMobile ? 72 : 88;
        const textBoxMaxHeight = isMobile ? 260 : 360;
        const textBoxVerticalPadding = isMobile ? 32 : 40;
        const textBoxContentHeight = textInputHeight + textBoxVerticalPadding;
        const textBoxHeight = Math.min(Math.max(textBoxMinHeight, textBoxContentHeight), textBoxMaxHeight);
        const textBoxShouldScroll = textBoxContentHeight > textBoxMaxHeight;
        return (
          <View
            style={[
              styles.textBoxShell,
              effectiveIsOwner ? styles.textBoxShellOwner : styles.textBoxShellViewer,
              effectiveIsOwner && isTextFocused ? styles.textBoxShellOwnerFocused : null,
              styles.widgetShadow,
              effectiveIsOwner
                ? { minHeight: textBoxMinHeight, height: textBoxHeight, overflow: 'hidden' }
                : { minHeight: textBoxMinHeight, overflow: 'visible' }
            ]}
          >
            {effectiveIsOwner && !isTextFocused && (
              <View style={styles.textBoxEditHint} pointerEvents="none">
                <Ionicons name="pencil-outline" size={isMobile ? 13 : 15} color="rgba(76, 149, 108, 0.75)" />
                <Text style={styles.textBoxEditHintText}>
                  {String(text || '').trim() ? 'Tap to edit' : 'Tap to add a message'}
                </Text>
              </View>
            )}
            {effectiveIsOwner ? (
              <TextInput
                value={text}
                onChangeText={handleTextChange}
                style={[
                  styles.textInputOwner,
                  pageTextColor ? { color: pageTextColor } : null,
                  { overflowY: textBoxShouldScroll ? 'auto' : 'hidden' }
                ]}
                multiline
                scrollEnabled={textBoxShouldScroll}
                placeholder=""
                placeholderTextColor="rgba(76, 149, 108, 0.45)"
                onFocus={() => setIsTextFocused(true)}
                onBlur={() => setIsTextFocused(false)}
                onContentSizeChange={(e) => handleContentSizeChange(e.nativeEvent.contentSize.width, e.nativeEvent.contentSize.height)}
              />
            ) : (
              <Text style={[styles.textInputViewer, pageTextColor ? { color: pageTextColor } : null]}>{text}</Text>
            )}
          </View>
        );
      case "ImageGallery":
        // Don't render an empty gallery for visitors — they'd see a blank transparent block.
        if (!effectiveIsOwner && (!images || images.length === 0)) return null;
        return (
          <View style={{ width: '100%', alignSelf: 'stretch' }}>
            <ImageGallery
              isEditMode={effectiveIsOwner}
              initialImages={images}
              isPreviewMode={isPreviewMode}
              maxImages={myTasselPlus.getFeatureLimit(MYTASSEL_PLUS_FEATURES.SLIDESHOW_IMAGES)}
              onLimitReached={() => myTasselPlus.openUpsell(MYTASSEL_PLUS_FEATURES.SLIDESHOW_IMAGES)}
              showPlusBadge={!myTasselPlus.hasPlus}
              onImagesUpdated={(updatedImages) => {
                setImages(updatedImages);
                setHasChanges(true);
              }}
            />
            {effectiveIsOwner && (
              <>
                <input
                  id={`fileInput-${widgetIndex}`}
                  type="file"
                  onChange={handleImageUpload}
                  style={styles.fileInput}
                />
                {loadingMessage && <Text>{loadingMessage}</Text>}
                {successMessage && <Text>{successMessage}</Text>}
                {errorMessage && <Text>{errorMessage}</Text>}
              </>
            )}
          </View>
        );
      case "PictureFrame":
        // Don't render an empty picture frame for visitors — they'd see a blank transparent block.
        if (!effectiveIsOwner && (!images || images.length === 0)) return null;
        const canAddPolaroid = myTasselPlus.canAddItems(MYTASSEL_PLUS_FEATURES.POLAROID_IMAGES, images.length, 1);
        return (
          <View style={[styles.pictureFrameContainer]}>
            {effectiveIsOwner && (images.length < 4 || !canAddPolaroid || myTasselPlus.hasPlus) && (
              <Pressable
                style={styles.fileInputButton}
                onPress={() => myTasselPlus.requireItemCapacity(
                  MYTASSEL_PLUS_FEATURES.POLAROID_IMAGES,
                  images.length,
                  1,
                  () => document.getElementById(`fileInput-${widgetIndex}`).click()
                )}
              >
                <Text style={styles.fileInputButtonText}>Add Photos {!canAddPolaroid ? "+" : ""}</Text>
              </Pressable>
            )}
            <input
              id={`fileInput-${widgetIndex}`}
              type="file"
              onChange={handleImageUpload}
              style={styles.fileInput}
            />
            <View style={styles.pictureFrameImages}>
              {images.slice(0, 4).map((img, index) => (
                <View key={index} style={styles.polaroid(index)}>
                  <Image source={{ uri: img }} style={[styles.tiltedImage, styles.widgetShadow]} />
                  {effectiveIsOwner && (
                    <Pressable onPress={() => handleRemoveImage(index)} style={styles.removeImageButton}>
                      <Text style={styles.removeButtonText}>Remove Image</Text>
                    </Pressable>
                  )}
                </View>
              ))}
            </View>
          </View>
        );
      case "RSVP":
        // Legacy RSVP widgets are deprecated - redirect users to use Event widgets instead
        return (
          <View style={[styles.eventContainer, styles.widgetShadow]}>
            <View style={styles.eventHeader}>
              <Text style={styles.eventTitle}>RSVP Widget (Deprecated)</Text>
            </View>
            <View style={styles.noEventMessage}>
              <Text style={styles.noEventText}>
                RSVP functionality has been moved to Event widgets. Please use the Event widget instead for better integration.
              </Text>
              {effectiveIsOwner && (
                <Pressable 
                  style={[styles.editButton, { marginTop: 15 }]} 
                  onPress={() => {
                    alert('Please delete this RSVP widget and create an Event widget instead. Event widgets now include built-in RSVP functionality.');
                  }}
                >
                  <Text style={styles.editButtonText}>Switch to Event Widget</Text>
                </Pressable>
              )}
            </View>
          </View>
        );
      case "URLPreview":
        if (!card.metadata && !effectiveIsOwner) {
          return null;
        }
        return (
          <View>
            {card.metadata ? (
              <Pressable onPress={() => Linking.openURL(card.url.startsWith("http") ? card.url : `https://${card.url}`)} style={styles.metadataContainer}>
                <Image source={{ uri: customImageUrl || card.metadata.image || "https://via.placeholder.com/600" }} style={styles.metadataImage} />
                <Text style={styles.metadataTitle}>{card.metadata.title}</Text>
              </Pressable>
            ) : (
              <Text style={styles.metadataTitle}>No metadata available</Text>
            )}
            
            {effectiveIsOwner && (
              <>
                <TextInput
                  value={url}
                  onChangeText={(text) => handleUrlChange(text)}
                  style={[styles.textInput, styles.widgetShadow]}
                  placeholder="Enter URL..."
                />

                <Pressable onPress={handleFetchMetadata} style={styles.fileInputButton1}>
                  <Text style={styles.fileInputButtonText}>Get URL</Text>
                </Pressable>

                <Pressable
                  style={[styles.fileInputButton1, !card.metadata && styles.disabledButton]}
                  onPress={() => card.metadata && document.getElementById(`fileInput-${widgetIndex}`).click()}
                  disabled={!card.metadata}
                >
                  <Text style={styles.fileInputButtonText}>Add a Cover Photo</Text>
                </Pressable>

                <input
                  id={`fileInput-${widgetIndex}`}
                  type="file"
                  onChange={handleImageUpload}
                  style={styles.fileInput}
                />

                {isLoading && <Text>Loading...</Text>} 

                {customImageUrl && (
                  <Pressable onPress={handleRemoveImage} style={styles.removeImageButton}>
                    <Text style={styles.removeButtonText}>Remove Image</Text>
                  </Pressable>
                )}
              </>
            )}
          </View>
        );
      case "Countdown":
        return (
          <View>
            <Countdown
              targetDate={targetDate.toISOString()}
              onCompleteMessage={onCompleteMessage}
              title="Countdown"
              isOwner={effectiveIsOwner}
              isEditing={true}
              onMessageChange={(message) => {
                setOnCompleteMessage(message);
                setHasChanges(true);
              }}
            />
            {effectiveIsOwner && (
              <View style={{ marginTop: 10, padding: 10 }}>
                <TextInput
                  value={onCompleteMessage}
                  onChangeText={handleCompleteMessageChange}
                  style={styles.textInput}
                  placeholder="Enter completion message..."
                  placeholderTextColor="#999"
                />

                {/* Inline Event Editor replaced by central `EventsEditor` component */}
                {isEditingEvent && (
                    <EventsEditor
                      eventDetails={eventDetails}
                      setEventDetails={setEventDetails}
                      isEditingEvent={isEditingEvent}
                      setIsEditingEvent={setIsEditingEvent}
                      handleLocationChange={handleLocationChange}
                      locationSuggestions={locationSuggestions}
                      showLocationSuggestions={showLocationSuggestions}
                      setShowLocationSuggestions={setShowLocationSuggestions}
                      selectLocation={selectLocation}
                      modalMapContainerRef={modalMapContainerRef}
                      saveEventDetails={(overrides) => saveEventDetails({
                        targetWidgetId: card.id,
                        targetWidgetIndex: widgetIndex,
                        ...(overrides || {}),
                      })}
                      formatDateTimeForInput={formatDateTimeForInput}
                      rsvpEnabled={rsvpEnabled}
                      setRsvpEnabled={setRsvpEnabled}
                      reminderSettings={reminderSettings}
                      setReminderSettings={setReminderSettings}
                      eventPhotos={eventPhotos}
                      onPhotosChange={handlePhotosChange}
                      uploadingPhotos={uploadingPhotos}
                      setUploadingPhotos={setUploadingPhotos}
                      pageOwnerId={pageOwnerId}
                      pageOwner={pageOwner}
                      eventUrl={eventUrl}
                      onUrlChange={setEventUrl}
                      featureFlags={featureFlags}
                      myTasselPlus={myTasselPlus}
                      eventImagesFeature={MYTASSEL_PLUS_FEATURES.EVENT_IMAGES}
                      localTitle={localTitle}
                      localDescription={localDescription}
                      setLocalTitle={setLocalTitle}
                      setLocalDescription={setLocalDescription}
                      debouncedSetTitle={debouncedSetTitle}
                      debouncedSetDescription={debouncedSetDescription}
                      lastSelectedLocationRef={lastSelectedLocationRef}
                      setErrorMessage={setErrorMessage}
                    />
                )} 

              </View>
            )}
          </View>
        );
      case "Poll":
        if (!featureFlags?.showPollsWidget) return null;
        return (
          <Poll
            card={card}
            isOwner={isOwner && !isVisitorView}
            isVisitorView={isVisitorView}
            isPreviewMode={isPreviewMode}
            widgetIndex={widgetIndex}
            currentUserData={currentUserData}
            pageOwnerId={pageOwnerId}
            setHasChanges={setHasChanges}
          />
        );
      case "Event":
        return (
          <View style={[styles.eventContainer, styles.widgetShadow]}>

            {/* ===== EMPTY STATE: No event created yet ===== */}
            {!eventDetails.title && !isEditingEvent ? (
              effectiveIsOwner ? (
                <View style={styles.emptyEventCard}>
                  {/* Decorative floating bubbles */}
                  <View style={[styles.decorBubble, { top: -30, right: -20, width: 120, height: 120, borderRadius: 60, backgroundColor: 'rgba(76, 149, 108, 0.08)' }]} />
                  <View style={[styles.decorBubble, { bottom: -20, left: -15, width: 80, height: 80, borderRadius: 40, backgroundColor: 'rgba(76, 149, 108, 0.06)' }]} />
                  <View style={[styles.decorBubble, { top: 30, left: 40, width: 50, height: 50, borderRadius: 25, backgroundColor: 'rgba(76, 149, 108, 0.04)' }]} />

                  <View style={styles.emptyEventContent}>
                    <Text style={styles.emptyEventTitle}>Plan Your Celebration</Text>
                    <Text style={styles.emptyEventSubtitle}>
                      Set up an event with location, date, RSVP tracking, and reminders for your guests.
                    </Text>
                    <Pressable
                      style={({ hovered }) => [
                        styles.createEventButton,
                        !isMobile && hovered && styles.createEventButtonHover
                      ]}
                      onPress={() => setIsEditingEvent(true)}
                    >
                      <Text style={styles.createEventButtonText}>Create Event</Text>
                    </Pressable>
                  </View>
                </View>
              ) : null /* Visitors see nothing if no event */
            ) : isEditingEvent ? (
              /* ===== EDITING STATE ===== */
              <EventsEditor
                eventDetails={eventDetails}
                setEventDetails={setEventDetails}
                isEditingEvent={isEditingEvent}
                setIsEditingEvent={setIsEditingEvent}
                handleLocationChange={handleLocationChange}
                locationSuggestions={locationSuggestions}
                showLocationSuggestions={showLocationSuggestions}
                setShowLocationSuggestions={setShowLocationSuggestions}
                selectLocation={selectLocation}
                modalMapContainerRef={modalMapContainerRef}
                saveEventDetails={(overrides) => saveEventDetails({
                  targetWidgetId: card.id,
                  targetWidgetIndex: widgetIndex,
                  ...(overrides || {}),
                })}
                formatDateTimeForInput={formatDateTimeForInput}
                rsvpEnabled={rsvpEnabled}
                setRsvpEnabled={setRsvpEnabled}
                reminderSettings={reminderSettings}
                setReminderSettings={setReminderSettings}
                eventPhotos={eventPhotos}
                onPhotosChange={handlePhotosChange}
                uploadingPhotos={uploadingPhotos}
                setUploadingPhotos={setUploadingPhotos}
                pageOwnerId={pageOwnerId}
                eventUrl={eventUrl}
                onUrlChange={setEventUrl}
                isPreviewMode={isPreviewMode}
                featureFlags={featureFlags}
                myTasselPlus={myTasselPlus}
                eventImagesFeature={MYTASSEL_PLUS_FEATURES.EVENT_IMAGES}
                localTitle={localTitle}
                localDescription={localDescription}
                setLocalTitle={setLocalTitle}
                setLocalDescription={setLocalDescription}
                debouncedSetTitle={debouncedSetTitle}
                debouncedSetDescription={debouncedSetDescription}
                lastSelectedLocationRef={lastSelectedLocationRef}
                setErrorMessage={setErrorMessage}
              />
            ) : (
              /* ===== EVENT DISPLAY STATE ===== */
              <View>
                {/* Green header banner with decorative bubbles */}
                <View style={styles.eventBanner}>
                  <View style={[styles.decorBubble, { top: -25, right: -25, width: 100, height: 100, borderRadius: 50, backgroundColor: 'rgba(255,255,255,0.1)' }]} />
                  <View style={[styles.decorBubble, { bottom: -15, left: -10, width: 70, height: 70, borderRadius: 35, backgroundColor: 'rgba(255,255,255,0.07)' }]} />
                  <View style={[styles.decorBubble, { top: 10, right: 80, width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.05)' }]} />

                  <View style={styles.eventBannerContent}>
                    <Text style={styles.eventBannerTitle}>{eventDetails.title}</Text>
                    {eventDetails.date ? (
                      <View style={styles.eventBannerDateRow}>
                        <Text style={styles.eventBannerDateText}>
                          {formatDateForDisplay(eventDetails.date)}
                        </Text>
                        {formatTimeForDisplay(eventDetails.date) ? (
                          <Text style={styles.eventBannerTimeText}>
                            {formatTimeForDisplay(eventDetails.date)}
                            {eventDetails.endDate && formatTimeOnly(eventDetails.endDate) ? ` — ${formatTimeOnly(eventDetails.endDate)}` : ''}
                          </Text>
                        ) : null}
                      </View>
                    ) : null}
                  </View>

                  {/* Live countdown in banner */}
                  {countdownTime && (
                    <View style={styles.bannerCountdown}>
                      {[
                        { val: countdownTime.days, lbl: 'days' },
                        { val: countdownTime.hours, lbl: 'hrs' },
                        { val: countdownTime.mins, lbl: 'min' },
                        { val: countdownTime.secs, lbl: 'sec' }
                      ].map((t, i) => (
                        <View key={i} style={styles.bannerCountdownBlock}>
                          <Text style={styles.bannerCountdownNum}>{t.val}</Text>
                          <Text style={styles.bannerCountdownLabel}>{t.lbl}</Text>
                        </View>
                      ))}
                    </View>
                  )}

                  {/* Owner controls on banner */}
                  {effectiveIsOwner && (
                    <View style={styles.eventBannerActions}>
                      {(() => {
                        const totalResponses = localRsvpCounts.going.length + localRsvpCounts.maybe.length + localRsvpCounts.decline.length;
                        return totalResponses > 0 && (
                          <Pressable
                            style={styles.bannerActionBtn}
                            onPress={() => setShowRSVPModal(true)}
                          >
                            <Text style={styles.bannerActionText}>Responses ({totalResponses})</Text>
                          </Pressable>
                        );
                      })()}
                      <Pressable
                        style={styles.bannerActionBtn}
                        onPress={() => {
                          setIsEditingEvent(true);
                          setEventDetails(prev => ({
                            ...prev,
                            title: card.eventTitle || card.title || prev.title,
                            date: card.eventDate || card.datetime || prev.date,
                            endDate: card.eventEndDate || card.endDatetime || prev.endDate || '',
                            location: card.eventLocation || card.location || prev.location,
                            description: card.eventDescription || card.description || prev.description,
                            locationCoords: card.locationCoords || prev.locationCoords
                          }));
                        }}
                      >
                        <Text style={styles.bannerActionText}>Edit</Text>
                      </Pressable>
                    </View>
                  )}
                </View>

                {/* Event details body */}
                <View style={styles.eventBody}>
                  {/* Description — shown first, larger font */}
                  {eventDetails.description ? (
                    <View style={styles.eventDetailRowStyled}>
                      <Text style={[styles.eventDescriptionTextStyled, pageTextColor ? { color: pageTextColor } : null]}>{eventDetails.description}</Text>
                    </View>
                  ) : null}

                  {/* Location - clickable to Google Maps */}
                  {eventDetails.location ? (
                    <Pressable
                      onPress={() => {
                        const q = eventDetails.locationCoords
                          ? `${eventDetails.locationCoords.lat},${eventDetails.locationCoords.lng}`
                          : encodeURIComponent(eventDetails.location);
                        Linking.openURL(`https://www.google.com/maps/search/?api=1&query=${q}`);
                      }}
                      style={({ hovered }) => [styles.eventDetailRowStyled, { marginTop: 12 }, !isMobile && hovered && { backgroundColor: 'rgba(76, 149, 108, 0.06)', borderRadius: 10, marginHorizontal: -6, paddingHorizontal: 6 }]}
                    >
                      <View style={{ flex: 1 }}>
                        <Text style={[styles.eventDetailTextStyled, pageTextColor ? { color: pageTextColor } : null]}>{eventDetails.location}</Text>
                        <Text style={{ fontSize: 11, color: '#4c956c', fontFamily: 'Orpheus Pro', marginTop: 2, textAlign: 'center' }}>Tap to view in Google Maps</Text>
                      </View>
                    </Pressable>
                  ) : null}

                  {/* Event URL link */}
                  {eventUrl ? (
                    <Pressable
                      onPress={() => {
                        const href = eventUrl.startsWith('http') ? eventUrl : `https://${eventUrl}`;
                        Linking.openURL(href);
                      }}
                      style={({ hovered }) => [styles.eventDetailRowStyled, { marginTop: 12 }, !isMobile && hovered && { backgroundColor: 'rgba(76, 149, 108, 0.06)', borderRadius: 10, marginHorizontal: -6, paddingHorizontal: 6 }]}
                    >
                      <View style={{ flex: 1 }}>
                        <Text style={[styles.eventDetailTextStyled, { color: '#4c956c', textDecorationLine: 'underline' }]} numberOfLines={1}>{eventUrl}</Text>
                      </View>
                    </Pressable>
                  ) : null}

                  {/* RSVP — after event details, above photos */}
                  {rsvpEnabled && (
                    <View style={styles.rsvpSectionStyled}>
                      <Text style={styles.rsvpSectionLabel}>Will you be attending?</Text>
                      <View style={styles.rsvpButtonRow}>
                        <Pressable
                          onPress={() => handleRSVP("Going")}
                          style={({ hovered }) => [
                            styles.rsvpBtnStyled,
                            rsvp === "Going"
                              ? styles.rsvpBtnGoing
                              : { borderColor: '#4c956c', backgroundColor: 'rgba(76, 149, 108, 0.08)' },
                            !isMobile && hovered && rsvp !== "Going" && { backgroundColor: 'rgba(76, 149, 108, 0.18)' }
                          ]}
                        >
                          <Text style={[styles.rsvpBtnText, rsvp === "Going" ? { color: '#fff' } : { color: '#4c956c' }]}>
                            I'll be there {localRsvpCounts.going.length ? `(${localRsvpCounts.going.length})` : ''}
                          </Text>
                        </Pressable>

                        <Pressable
                          onPress={() => handleRSVP("Maybe")}
                          style={({ hovered }) => [
                            styles.rsvpBtnStyled,
                            rsvp === "Maybe"
                              ? styles.rsvpBtnMaybe
                              : { borderColor: '#d97706', backgroundColor: 'rgba(217, 119, 6, 0.08)' },
                            !isMobile && hovered && rsvp !== "Maybe" && { backgroundColor: 'rgba(217, 119, 6, 0.18)' }
                          ]}
                        >
                          <Text style={[styles.rsvpBtnText, rsvp === "Maybe" ? { color: '#fff' } : { color: '#d97706' }]}>
                            Maybe {localRsvpCounts.maybe.length ? `(${localRsvpCounts.maybe.length})` : ''}
                          </Text>
                        </Pressable>

                        <Pressable
                          onPress={() => handleRSVP("Decline")}
                          style={({ hovered }) => [
                            styles.rsvpBtnStyled,
                            rsvp === "Decline"
                              ? styles.rsvpBtnDecline
                              : { borderColor: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.08)' },
                            !isMobile && hovered && rsvp !== "Decline" && { backgroundColor: 'rgba(239, 68, 68, 0.18)' }
                          ]}
                        >
                          <Text style={[styles.rsvpBtnText, rsvp === "Decline" ? { color: '#fff' } : { color: '#ef4444' }]}>
                            Can't Go {localRsvpCounts.decline.length ? `(${localRsvpCounts.decline.length})` : ''}
                          </Text>
                        </Pressable>
                      </View>
                    </View>
                  )}

                  {/* Event Photos Slideshow */}
                  {eventPhotos.length > 0 && (
                    <View style={{ width: '100%' }}>
                      <EventPhotoSlideshow photos={eventPhotos} />
                      {/* Remove photo buttons for owner */}
                      {effectiveIsOwner && (
                        <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                          {eventPhotos.map((_, idx) => (
                            <Pressable
                              key={idx}
                              onPress={async () => {
                                if (!myTasselPlus.requireFeature(MYTASSEL_PLUS_FEATURES.EVENT_IMAGES)) return;
                                const updated = eventPhotos.filter((__, i) => i !== idx);
                                setEventPhotos(updated);
                                if (isPreviewMode) {
                                  if (setCards) {
                                    setCards(currentCards => currentCards.map(currentCard => (
                                      currentCard.id === card.id ? { ...currentCard, eventPhotos: updated } : currentCard
                                    )));
                                  }
                                  return;
                                }
                                try {
                                  const db = getFirestore();
                                  const userQuery = query(collection(db, "users"), where("uid", "==", pageOwnerId));
                                  const qs = await getDocs(userQuery);
                                  if (!qs.empty) {
                                    const docSnap = qs.docs[0];
                                    const data = docSnap.data();
                                    const cards = data.pages?.eventsPageConfig?.cards || [];
                                    const rmPhotoIdx = cards.findIndex(c => c.id === card.id);
                                    if (rmPhotoIdx !== -1) {
                                      cards[rmPhotoIdx].eventPhotos = updated;
                                      const cleanedPhotoCards = cleanForFirestore(cards);
                                      await updateDoc(doc(db, "users", docSnap.id), { 'pages.eventsPageConfig.cards': cleanedPhotoCards });
                                    }
                                  }
                                } catch (err) { console.error('Error removing photo:', err); }
                              }}
                              style={{ backgroundColor: 'rgba(239,68,68,0.1)', borderRadius: 8, paddingHorizontal: 8, paddingVertical: 4, flexDirection: 'row', alignItems: 'center', gap: 4 }}
                            >
                              <Text style={{ fontSize: 11, color: '#ef4444' }}>Remove Photo {idx + 1}</Text>
                            </Pressable>
                          ))}
                        </View>
                      )}
                    </View>
                  )}


                  {/* Photo upload for owner - iOS Safari compatible styled file input */}
                  {effectiveIsOwner && (
                    <View style={{ position: 'relative', width: '100%', marginTop: 16 }}>
                      {canUseEventImages ? (
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          disabled={uploadingPhotos}
                          onChange={async (e) => {
                            console.log('🎯 File input onChange triggered!');
                            const files = Array.from(e.target.files || []);
                            console.log('📁 Files selected:', files.length);

                            if (files.length === 0) {
                              console.log('⚠️ No files selected');
                              return;
                            }

                            console.log('✅ Starting upload for', files.length, 'files');
                            setUploadingPhotos(true);
                            setUploadProgress(`Preparing ${files.length} photo${files.length > 1 ? 's' : ''}...`);

                            try {
                              const assets = files.map(file => ({
                                uri: URL.createObjectURL(file),
                                type: file.type,
                                fileName: file.name,
                                fileSize: file.size,
                                file: file,
                              }));

                              console.log('📦 Assets prepared:', assets.length);
                              await uploadSelectedPhotos(assets);
                              console.log('✅ Upload complete');
                            } catch (error) {
                              console.error('❌ Upload error:', error);
                              setUploadingPhotos(false);
                              setUploadProgress('');
                            }

                            // Reset input
                            e.target.value = '';
                          }}
                          onClick={() => console.log('👆 File input clicked directly')}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            opacity: 0,
                            cursor: uploadingPhotos ? 'not-allowed' : 'pointer',
                            zIndex: 2,
                          }}
                        />
                      ) : (
                        <Pressable
                          disabled={uploadingPhotos}
                          onPress={() => myTasselPlus.openUpsell(MYTASSEL_PLUS_FEATURES.EVENT_IMAGES)}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            zIndex: 2,
                            cursor: uploadingPhotos ? 'not-allowed' : 'pointer',
                          }}
                        />
                      )}
                      <View style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        paddingVertical: 10,
                        paddingHorizontal: 14,
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: 'rgba(76, 149, 108, 0.15)',
                        borderStyle: 'dashed',
                        backgroundColor: uploadingPhotos ? 'rgba(76, 149, 108, 0.08)' : 'transparent',
                        opacity: uploadingPhotos ? 0.6 : 1,
                        pointerEvents: 'none',
                      }}>
                        <Text style={{ fontSize: 13, color: '#4c956c', fontWeight: '600', fontFamily: 'Orpheus Pro' }}>
                          {uploadingPhotos ? uploadProgress : `${eventPhotos.length > 0 ? 'Add More Photos' : 'Add Event Photos'} ${!canUseEventImages ? '+' : ''}`}
                        </Text>
                      </View>
                    </View>
                  )}
                </View>

                {/* Map display — gated behind showVisitorMap feature flag */}
                {featureFlags?.showVisitorMap && eventDetails.locationCoords && (
                  <Pressable
                    style={styles.eventMapContainer}
                    onPress={() => {
                      const q = `${eventDetails.locationCoords.lat},${eventDetails.locationCoords.lng}`;
                      Linking.openURL(`https://www.google.com/maps/search/?api=1&query=${q}`);
                    }}
                  >
                    <div ref={viewMapContainerRef} style={{ width: '100%', height: '100%', borderRadius: 0, overflow: 'hidden' }} />
                  </Pressable>
                )}
              </View>
            )}

            {/* RSVP Modal */}
            {showRSVPModal && (
              <SharedModal
                isVisible={showRSVPModal}
                onClose={() => setShowRSVPModal(false)}
                title="RSVP Responses"
              >
                {/* Implement RSVP responses view similar to RSVPWidgets */}
                <View style={styles.rsvpModalContent}>
                  <View style={styles.rsvpTabs}>
                    {['Going', 'Maybe', 'Decline'].map(tab => {
                      const tabLabel = tab === 'Going' ? "I'll be there" : tab;
                      return (
                        <Pressable
                          key={tab}
                          style={[styles.rsvpTab, activeRSVPTab === tab && styles.activeRsvpTab]}
                          onPress={() => setActiveRSVPTab(tab)}
                        >
                          <Text style={[styles.rsvpTabText, activeRSVPTab === tab && styles.activeRsvpTabText]}>
                            {tabLabel} ({localRsvpCounts[tab.toLowerCase()]?.length || 0})
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>

                  <View style={styles.rsvpUsersList}>
                    {getRSVPList(activeRSVPTab.toLowerCase()).map((user, index) => (
                      <View key={index} style={styles.rsvpUserItem}>
                        <Text style={styles.rsvpUserName}>
                          {user.firstName || user.lastName 
                            ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                            : user.email || 'Anonymous User'
                          }
                        </Text>
                        {user.email && (
                          <Text style={styles.rsvpUserEmail}>{user.email}</Text>
                        )}
                        {user.phone && (
                          <Text style={styles.rsvpUserPhone}>{user.phone}</Text>
                        )}
                      </View>
                    ))}

                    {getRSVPList(activeRSVPTab.toLowerCase()).length === 0 && (
                      <Text style={styles.noRsvpText}>
                        No {activeRSVPTab.toLowerCase()} responses yet
                      </Text>
                    )}
                  </View>
                </View>
              </SharedModal>
            )}

            {/* Guest Info Modal */}
            {showGuestInfoModal && (
              <SharedModal
                isVisible={showGuestInfoModal}
                onClose={() => {
                  setShowGuestInfoModal(false);
                  setGuestInfo({ firstName: "", lastName: "", email: "", phone: "" });
                  setTempRSVPSelection(null);
                }}
                title="Guest Information"
                hideButtons
              >
                <div style={{ width: '100%' }}>
                  <p style={{ color: '#666', fontSize: 14, marginBottom: 20, marginTop: 0, textAlign: 'center', fontFamily: 'Orpheus Pro, serif' }}>
                    Please provide your information to RSVP
                  </p>

                  <input
                    style={guestInputStyle}
                    value={guestInfo.firstName}
                    onChange={(e) => setGuestInfo(prev => ({ ...prev, firstName: e.target.value }))}
                    placeholder="First Name *"
                  />
                  <input
                    style={guestInputStyle}
                    value={guestInfo.lastName}
                    onChange={(e) => setGuestInfo(prev => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Last Name *"
                  />
                  <input
                    style={guestInputStyle}
                    value={guestInfo.email}
                    onChange={(e) => setGuestInfo(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Email Address *"
                    type="email"
                  />
                  <input
                    style={guestInputStyle}
                    value={guestInfo.phone}
                    onChange={(e) => setGuestInfo(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="Phone Number — optional, e.g. (555) 867-5309"
                    type="tel"
                  />
                  <p style={{ color: '#aaa', fontSize: 11, marginTop: 0, marginBottom: 16, textAlign: 'center', fontFamily: 'Orpheus Pro, serif' }}>
                    US numbers work without +1 &bull; Used only for SMS event reminders
                  </p>

                  <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                    <button
                      style={{
                        ...guestModalButtonBase,
                        backgroundColor: '#e8e8e8',
                        color: '#666',
                        opacity: isSubmittingRSVP ? 0.5 : 1,
                        cursor: isSubmittingRSVP ? 'not-allowed' : 'pointer',
                      }}
                      onClick={() => {
                        if (isSubmittingRSVP) return;
                        setShowGuestInfoModal(false);
                        setGuestInfo({ firstName: "", lastName: "", email: "", phone: "" });
                        setTempRSVPSelection(null);
                      }}
                      disabled={isSubmittingRSVP}
                    >
                      Cancel
                    </button>
                    <button
                      style={{
                        ...guestModalButtonBase,
                        backgroundColor: (isSubmittingRSVP || !guestInfo.firstName || !guestInfo.lastName || !guestInfo.email) ? '#ccc' : '#4c956c',
                        color: '#fefde2',
                        cursor: (isSubmittingRSVP || !guestInfo.firstName || !guestInfo.lastName || !guestInfo.email) ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                      }}
                      onClick={handleGuestInfoSubmit}
                      disabled={isSubmittingRSVP || !guestInfo.firstName || !guestInfo.lastName || !guestInfo.email}
                    >
                      {isSubmittingRSVP ? (
                        <>
                          <span style={{
                            width: 14,
                            height: 14,
                            border: '2px solid rgba(254,253,226,0.4)',
                            borderTopColor: '#fefde2',
                            borderRadius: '50%',
                            display: 'inline-block',
                            animation: 'spin 0.7s linear infinite',
                          }} />
                          Submitting…
                        </>
                      ) : 'Submit RSVP'}
                    </button>
                  </div>
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
              </SharedModal>
            )}
          </View>
        );
      default:
        return isOwner && !isVisitorView ? <Text style={styles.selectWidgetText}>Select a widget</Text> : null;
    }
  };

  const handleShowCatalogue = () => {
    // Logic to show the catalogue
    onToggleCatalogue(); // Toggle the catalogue visibility
  };

  return (
    <View {...spotlightTargetWhen(isOwner && !isVisitorView && isLastCard, SPOTLIGHT_TARGETS.WIDGET_CARD)} style={[styles.card, { width: cardWidth, maxWidth: cardMaxWidth, ...(isNarrowViewport && ['TextBox', 'ImageGallery', 'Event'].includes(selectedWidgetType) ? { paddingHorizontal: 0 } : {}) }]}>
      {isOwner && !isVisitorView && (() => {
        const widgetOptions = getAvailableWidgetOptions(featureFlags);
        const selected = widgetOptions.find(o => o.value === selectedWidgetType);
        return (
          <View {...spotlightTargetWhen(isLastCard && !selected, SPOTLIGHT_TARGETS.WIDGET_PICKER)} style={{ position: 'relative', zIndex: 100, alignSelf: 'center', width: isMobile ? '90%' : '50%', marginBottom: 8 }}>
            <Pressable
              onPress={() => setShowWidgetDropdown(!showWidgetDropdown)}
              style={({ hovered }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: (!isMobile && hovered) ? 'rgba(254, 253, 226, 1)' : 'rgba(254, 253, 226, 0.95)',
                borderRadius: 14,
                paddingHorizontal: 16,
                paddingVertical: 12,
                borderWidth: 1,
                borderColor: showWidgetDropdown ? 'rgba(76, 149, 108, 0.4)' : 'rgba(76, 149, 108, 0.15)',
                shadowColor: '#1a5c3a',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.08,
                shadowRadius: 6,
                elevation: 2,
              })}
            >
              <Text style={{ fontSize: 15, fontWeight: '600', color: selected ? '#2d5a3f' : '#999', fontFamily: 'Bodoni72Smallcaps' }}>
                {selected ? selected.label : 'Select a widget...'}
              </Text>
              <Text style={{ fontSize: 12, color: '#4c956c', fontFamily: 'Bodoni72Smallcaps' }}>{showWidgetDropdown ? '▲' : '▼'}</Text>
            </Pressable>

            {showWidgetDropdown && (
              <View style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: 4,
                backgroundColor: '#fff',
                borderRadius: 14,
                borderWidth: 1,
                borderColor: 'rgba(76, 149, 108, 0.15)',
                shadowColor: '#1a5c3a',
                shadowOffset: { width: 0, height: 8 },
                shadowOpacity: 0.12,
                shadowRadius: 16,
                elevation: 8,
                overflow: 'hidden',
                zIndex: 999,
              }}>
                {widgetOptions.map((option, idx) => (
                  <Pressable
                    key={option.value}
                    {...spotlightTarget(widgetOptionTarget(option.value))}
                    onPress={() => {
                      emitSpotlightEvent(SPOTLIGHT_EVENTS.ANNOUNCEMENT_WIDGET_SELECTED, { type: option.value });
                      handleWidgetTypeChange(option.value);
                      setShowWidgetDropdown(false);
                    }}
                    style={({ hovered }) => ({
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 12,
                      paddingHorizontal: 16,
                      paddingVertical: 12,
                      backgroundColor: selectedWidgetType === option.value ? 'rgba(76, 149, 108, 0.08)' : ((!isMobile && hovered) ? 'rgba(254, 253, 226, 0.5)' : 'transparent'),
                      borderBottomWidth: idx < widgetOptions.length - 1 ? 1 : 0,
                      borderBottomColor: 'rgba(76, 149, 108, 0.06)',
                    })}
                  >
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: 14, fontWeight: '600', color: '#2d5a3f', fontFamily: 'Bodoni72Smallcaps' }}>{option.label}</Text>
                      <Text style={{ fontSize: 11, color: '#999', fontFamily: 'Orpheus Pro' }}>{option.desc}</Text>
                    </View>
                    {selectedWidgetType === option.value && (
                      <Text style={{ fontSize: 14, color: '#4c956c', fontWeight: '700' }}>✓</Text>
                    )}
                  </Pressable>
                ))}
              </View>
            )}
          </View>
        );
      })()}
      {renderWidget()}
      {isOwner && (
        <>
          <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 12, marginVertical: 10 }}>
            {hasChanges && !isEditingEvent && (
              <Pressable
                style={({ hovered }) => [
                  styles.saveButton,
                  !isMobile && hovered && { backgroundColor: '#3d7d5a', transform: [{ scale: 1.03 }] }
                ]}
                onPress={() => saveCard()}
              >
                <Text style={styles.saveButtonText}>Save Changes</Text>
              </Pressable>
            )}
            <Pressable
              style={({ hovered }) => [
                styles.removeButton,
                !isMobile && hovered && !isRemovingWidget && { backgroundColor: '#e03e3e', transform: [{ scale: 1.03 }] },
                isRemovingWidget && { opacity: 0.5, cursor: 'not-allowed' }
              ]}
              onPress={isRemovingWidget ? null : handleRemoveEventWidget}
              disabled={isRemovingWidget}
            >
              <Text style={styles.buttonText}>
                {isRemovingWidget ? 'Removing...' : 'Remove Widget'}
              </Text>
            </Pressable>
          </View>
          {isLastCard && (
            <Pressable 
              {...spotlightTarget(SPOTLIGHT_TARGETS.WIDGET_ADD)}
              style={[
                styles.addButton,
                isAddingWidget && { opacity: 0.5, cursor: 'not-allowed' }
              ]} 
              onPress={isAddingWidget ? null : () => {
                emitSpotlightEvent(SPOTLIGHT_EVENTS.ANNOUNCEMENT_WIDGET_ADDED);
                onAdd("");
              }}
              disabled={isAddingWidget}
            >
              <Text style={styles.addButtonText}>
                {isAddingWidget ? '...' : ' + '}
              </Text>
            </Pressable>
          )}
        </>
      )}
      {successMessage && (
        <View style={styles.successMessageContainer}>
          <Text style={styles.successMessageText}>{successMessage}</Text>
        </View>
      )}
      <PromptSignUp
        isVisible={showSignUpPrompt}
        onClose={() => setShowSignUpPrompt(false)}
        originalPage="Events"
        redirectPage="SignUp"
      />
      {myTasselPlus.upsellModal}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    zIndex: 1001,
    width: "100%",
    maxWidth: isMobile ? '100%' : 800,
    marginVertical: 5,
    padding: 20,
    backgroundColor: "rgba(254, 253, 226, 0)",
    borderRadius: 15,
    justifyContent: "center",
    alignItems: 'center',
    alignSelf: 'center',
    minWidth: 0,
    boxSizing: 'border-box',
  },
  textInput: {
    padding: 10,
    borderColor: "rgba(0, 0, 0, 0.25)",
    borderWidth: 1,
    borderRadius: 5,
    marginVertical: 10,
    textAlign: 'center',
    backgroundColor: "rgba(254, 253, 226, 1)",
    fontFamily: fontFamilies.Bodoni72Smallcaps,
  },
  textBoxShell: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    alignSelf: "stretch",
    paddingHorizontal: isMobile ? 18 : 26,
    paddingVertical: isMobile ? 16 : 20,
    borderRadius: isMobile ? 14 : 18,
    marginVertical: 12,
    overflow: "hidden",
    boxSizing: 'border-box',
  },
  textBoxShellOwner: {
    borderColor: "rgba(76, 149, 108, 0.45)",
    borderWidth: 1.5,
    borderStyle: 'dashed',
    backgroundColor: 'rgba(254, 253, 226, 0.96)',
  },
  textBoxShellOwnerFocused: {
    borderColor: '#4c956c',
    borderWidth: 2,
    borderStyle: 'solid',
    backgroundColor: 'rgba(254, 253, 226, 1)',
    boxShadow: '0 0 0 3px rgba(76, 149, 108, 0.18), 0 2px 12px rgba(76, 149, 108, 0.12)',
  },
  textBoxEditHint: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    marginBottom: isMobile ? 6 : 8,
    pointerEvents: 'none',
  },
  textBoxEditHintText: {
    color: 'rgba(76, 149, 108, 0.85)',
    fontSize: isMobile ? 12 : 13,
    fontFamily: 'Orpheus Pro',
    fontStyle: 'italic',
    letterSpacing: 0.2,
  },
  textBoxShellViewer: {
    borderColor: "rgba(76, 149, 108, 0.18)",
    borderWidth: 1,
    backgroundColor: 'rgba(254, 253, 226, 0.96)',
  },
  textInputOwner: {
    flex: 1,
    width: "100%",
    height: "100%",
    padding: 0,
    borderWidth: 0,
    textAlign: 'center',
    backgroundColor: 'transparent',
    color: '#4c956c',
    minWidth: 0,
    fontSize: isMobile ? 16 : 22,
    justifyContent: "center",
    alignItems: "center",
    fontFamily: "Orpheus Pro",
    lineHeight: isMobile ? 24 : 32,
    verticalAlign: "middle",
    boxSizing: 'border-box',
    outlineStyle: 'none',
    resize: 'none',
  },
  textInputViewer: {
    width: "100%",
    minHeight: 0,
    padding: 0,
    borderWidth: 0,
    textAlign: 'center',
    backgroundColor: 'transparent',
    color: '#4c956c',
    minWidth: 0,
    fontSize: isMobile ? 16 : 22,
    justifyContent: "center",
    alignItems: "center",
    fontFamily: "Orpheus Pro",
    lineHeight: isMobile ? 24 : 32,
    verticalAlign: "middle",
    boxSizing: 'border-box',
    outlineStyle: 'none',
    resize: 'none',
  },
  image: {
    width: 100,
    height: 100,
    marginVertical: 10,
    borderRadius: 0,
    borderWidth: 2,
    borderColor: "rgba(0, 0, 0, 1)",
  },
  button: {
    padding: 10,
    backgroundColor: "#ccc",
    borderRadius: 5,
    marginVertical: 5,
  },
  selectedButton: {
    padding: 10,
    backgroundColor: "#4c956c",
    borderRadius: 5,
    marginVertical: 5,
  },
  removeButton: {
    backgroundColor: "#ff4c4c",
    padding: 10,
    borderRadius: 8,
    alignItems: "center",
    marginVertical: 10,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  },
  addButton: {
    backgroundColor: "#4c956c",
    padding: 10,
    borderRadius: 100,
    alignItems: "center",
    marginTop: 25,
  },
  addButtonText: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: 18
  },
  buttonText: {
    color: "#fff",
    fontWeight: "bold",
  },
  saveButton: {
    backgroundColor: "#4c956c",
    padding: 10,
    borderRadius: 8,
    alignItems: "center",
    marginVertical: 10,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  },
  saveButtonText: {
    color: "#fff",
    fontWeight: "bold",
  },
  removeImageButton: {
    backgroundColor: "#ff0000",
    padding: 5,
    borderRadius: 5,
    alignItems: "center",
    marginVertical: 5,
  },
  removeButtonText: {
    color: "#fff",
    fontSize: 14,
  },
  fileInputButton: {
    backgroundColor: '#4c956c',
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    marginTop: 10,
    marginBottom: 60,
    width: "50%",
    shadowColor: "rgba(0, 0, 0, 0.5)",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  fileInputButton1: {
    backgroundColor: '#4c956c',
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: "center",
    marginTop: 10,
    marginBottom: 60,
    flex: 1,
    width: "100%",
    shadowColor: "rgba(0, 0, 0, 0.5)",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  fileInputButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: "bold",
    alignItems: 'center',
  },
  fileInput: {
    display: 'none',
  },
  tiltedImage: {
    width: isMobile ? 250 : 400,
    height: isMobile ? 250 : 400,
    borderRadius: 10,
  },
  pictureFrameContainer: {
    width: "90vw",
    padding: 10,
    backgroundColor: "rgba(254, 253, 226, 0)",
    marginBottom: 20,
    paddingTop: 50,
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  pictureFrameImages: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    
  },
  polaroid: (index) => ({
    position: "relative",
    padding: 10,
    backgroundColor: "#fff",
    borderRadius: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.8,
    shadowRadius: 2,
    elevation: 5,
    transform: [{ rotate: `${index % 2 === 0 ? -10 : 10}deg` }],
  }),
  removeImageButton: {
    position: "absolute",
    top: 5,
    right: 5,
    backgroundColor: "#ff0000",
    padding: 5,
    borderRadius: 5,
    alignItems: "center",
  },
  removeButtonText: {
    color: "#fff",
    fontSize: 12,
  },
  picker: {
    width: '65%',
    height: 50,
    backgroundColor: '#4c956c',
    color: '#000',
    borderRadius: 5,
    borderColor: '#4c956c',
    marginVertical: 10,
    justifyContent: 'center',
    fontFamily: fontFamilies.Bodoni72Smallcaps,
    fontSize: isMobile ? 14 : 20,
    shadowColor: "rgba(0, 0, 0, 0.5)",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    backgroundColor: "#fefde2",
    padding: 10,
    borderRadius: 5,
    alignItems: "center",
    width: "50%"
  },  
  pickerItem: {
    width: "50%",
    fontSize: isMobile ? 16 : 20,
    fontFamily: fontFamilies.Bodoni72Smallcaps,
  },
  widgetShadow: {
    shadowColor: "rgba(0, 0, 0, 0.5)",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  metadataContainer: {
    alignItems: "center",
    marginBottom: 10,
  },
  metadataImage: {
    width: isMobile ? "90vw" : 1200,
    height: isMobile ? "50vh" : 700,
    borderRadius: 15,
  },
  metadataTitle: {
    marginTop: 10,
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
    color: "#fefde2", // Beige color
    fontFamily: "Orpheus Pro",
  },
  successMessageContainer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(76, 149, 108, 0.75)',
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  successMessageText: {
    color: 'white',
    fontWeight: 'bold',
  },
  disabledButton: {
    backgroundColor: "#ccc",
  },
  selectWidgetText: {
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
    color: "#fefde2", // Beige color
    fontFamily: "Orpheus Pro",
  },
  datePickerButton: {
    backgroundColor: '#4c956c',
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    marginVertical: 10,
  },
  datePickerButtonText: {
    color: '#fff',
    fontWeight: 'bold',
  },
  webDateTimeInput: {
    padding: 10,
    borderColor: '#ccc',
    borderWidth: 1,
    borderRadius: 5,
    marginVertical: 10,
    width: '100%',
  },
  modalTitle: {
    color: '#4c956c',
    marginBottom: '20px',
    fontSize: '24px',
    fontWeight: 'bold',
    fontFamily: 'Orpheus Pro, serif',
  },
  // Event Widget Styles
  eventContainer: {
    backgroundColor: 'rgba(254, 253, 226, 0.95)',
    borderRadius: 28,
    padding: 0,
    marginBottom: 10,
    minHeight: 200,
    width: '100%',
    alignSelf: 'stretch',
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(76, 149, 108, 0.12)',
  },

  // ===== DECORATIVE BUBBLES =====
  decorBubble: {
    position: 'absolute',
    zIndex: 0,
  },

  // ===== EMPTY STATE STYLES =====
  emptyEventCard: {
    position: 'relative',
    overflow: 'hidden',
    padding: isMobile ? 30 : 50,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 280,
  },
  emptyEventContent: {
    alignItems: 'center',
    zIndex: 1,
  },
  emptyEventIconCircle: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: 'rgba(76, 149, 108, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
    borderWidth: 2,
    borderColor: 'rgba(76, 149, 108, 0.15)',
  },
  emptyEventTitle: {
    fontSize: isMobile ? 22 : 26,
    fontWeight: 'bold',
    color: '#2d5a3f',
    fontFamily: fontFamilies.Bodoni72Smallcaps,
    marginBottom: 10,
    textAlign: 'center',
  },
  emptyEventSubtitle: {
    fontSize: 15,
    color: '#777',
    textAlign: 'center',
    fontFamily: 'Orpheus Pro',
    lineHeight: 22,
    maxWidth: 360,
    marginBottom: 24,
  },
  createEventButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#4c956c',
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 16,
    shadowColor: '#1a5c3a',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 4,
  },
  createEventButtonHover: {
    backgroundColor: '#3d7d5a',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.35,
    shadowRadius: 12,
  },
  createEventButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
    fontFamily: fontFamilies.Bodoni72Smallcaps,
  },

  // ===== EVENT BANNER (green header) =====
  eventBanner: {
    backgroundColor: '#4c956c',
    padding: isMobile ? 20 : 28,
    paddingBottom: isMobile ? 16 : 22,
    position: 'relative',
    overflow: 'hidden',
    alignItems: 'center',
  },
  eventBannerContent: {
    zIndex: 1,
    alignItems: 'center',
  },
  eventBannerTitle: {
    fontSize: isMobile ? 26 : 34,
    fontWeight: 'bold',
    color: '#fff',
    fontFamily: fontFamilies.Bodoni72Smallcaps,
    textShadow: '1px 1px 3px rgba(0, 0, 0, 0.15)',
    textAlign: 'center',
  },
  eventBannerDateRow: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
    gap: 2,
  },
  eventBannerDateText: {
    color: 'rgba(255,255,255,0.95)',
    fontSize: isMobile ? 17 : 20,
    fontFamily: 'Orpheus Pro',
    fontWeight: '600',
    textAlign: 'center',
  },
  eventBannerTimeText: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: isMobile ? 15 : 18,
    fontFamily: 'Orpheus Pro',
    textAlign: 'center',
    marginTop: 2,
  },
  eventBannerActions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 14,
    zIndex: 1,
    justifyContent: 'center',
  },
  bannerActionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: 'rgba(255,255,255,0.18)',
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.25)',
  },
  bannerActionText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },

  // ===== EVENT BODY =====
  eventBody: {
    padding: isMobile ? 16 : 24,
    alignItems: 'center',
  },
  eventDetailRowStyled: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    justifyContent: 'center',
  },
  eventDetailIcon: {
    width: 32,
    height: 32,
    borderRadius: 10,
    backgroundColor: 'rgba(76, 149, 108, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  eventDescriptionTextStyled: {
    fontSize: isMobile ? 17 : 19,
    color: '#333',
    fontFamily: 'Orpheus Pro',
    lineHeight: isMobile ? 26 : 28,
    flex: 1,
    textAlign: 'center',
  },
  eventDetailTextStyled: {
    fontSize: 15,
    color: '#444',
    fontFamily: 'Orpheus Pro',
    lineHeight: 22,
    flex: 1,
    paddingTop: 4,
    textAlign: 'center',
  },

  // ===== RSVP SECTION =====
  rsvpSectionStyled: {
    marginTop: 20,
    paddingTop: 18,
    borderTopWidth: 1,
    borderTopColor: 'rgba(76, 149, 108, 0.12)',
  },
  rsvpSectionLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: '#555',
    fontFamily: fontFamilies.Bodoni72Smallcaps,
    marginBottom: 12,
    textAlign: 'center',
  },
  rsvpButtonRow: {
    flexDirection: isMobile ? 'column' : 'row',
    gap: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  rsvpBtnStyled: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 18,
    paddingVertical: 11,
    borderRadius: 14,
    backgroundColor: 'rgba(245, 240, 225, 0.8)',
    borderWidth: 1,
    borderColor: 'rgba(76, 149, 108, 0.12)',
    minWidth: isMobile ? '80%' : 130,
    justifyContent: 'center',
  },
  rsvpBtnGoing: {
    backgroundColor: '#4c956c',
    borderColor: '#4c956c',
  },
  rsvpBtnMaybe: {
    backgroundColor: '#d97706',
    borderColor: '#d97706',
  },
  rsvpBtnDecline: {
    backgroundColor: '#ef4444',
    borderColor: '#ef4444',
  },
  rsvpBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    fontFamily: fontFamilies.Bodoni72Smallcaps,
  },

  // ===== COUNTDOWN IN BANNER =====
  bannerCountdown: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 14,
    zIndex: 1,
    justifyContent: 'center',
  },
  bannerCountdownBlock: {
    backgroundColor: 'rgba(255,255,255,0.18)',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.15)',
    minWidth: 56,
  },
  bannerCountdownNum: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#fff',
    fontFamily: fontFamilies.Bodoni72Smallcaps,
  },
  bannerCountdownLabel: {
    fontSize: 10,
    color: 'rgba(255,255,255,0.75)',
    fontFamily: 'Orpheus Pro',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },

  // ===== PHOTO BUTTON =====
  addPhotoBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(76, 149, 108, 0.15)',
    borderStyle: 'dashed',
    marginTop: 14,
    justifyContent: 'center',
  },
  addPhotoBtnText: {
    fontSize: 13,
    color: '#4c956c',
    fontWeight: '600',
    fontFamily: 'Orpheus Pro',
  },

  // ===== MAP (square with rounded edges) =====
  eventMapContainer: {
    width: '100%',
    aspectRatio: 1,
    maxHeight: isMobile ? 320 : 420,
    borderRadius: 0,
    overflow: 'hidden',
    borderTopWidth: 1,
    borderTopColor: 'rgba(76, 149, 108, 0.12)',
    cursor: 'pointer',
  },

  eventHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  eventTitle: {
    fontSize: isMobile ? 20 : 24,
    fontWeight: 'bold',
    color: '#4c956c',
    fontFamily: fontFamilies.Bodoni72Smallcaps,
    flex: 1,
  },
  editButton: {
    backgroundColor: '#4c956c',
    paddingHorizontal: 15,
    paddingVertical: 8,
    borderRadius: 20,
    marginLeft: 10,
  },
  editButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  // Owner edit button container 
  ownerTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    alignSelf: 'flex-end',
    marginBottom: 8,
  },
  // Two-column event layout
  eventTopRow: {
    flexDirection: 'row',
    marginTop: 20,
    gap: 20,
  },
  leftColumn: {
    flex: 7,
    paddingVertical: 8,
    justifyContent: 'flex-start',
  },
  rightColumn: {
    flex: 3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  leftTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  inlineInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  inlineInfoText: {
    fontSize: 14,
    color: '#666',
    fontFamily: 'Orpheus Pro',
  },
  dotSeparator: {
    marginHorizontal: 8,
    color: '#666',
  },
  rsvpSectionCompact: {
    flexDirection: isMobile ? 'column' : 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    alignContent: 'center',
    gap: 10,
    paddingHorizontal: 8,
    width: '100%'
  },
  eventDetails: {
    marginBottom: 15,
  },
  eventDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  eventDetailText: {
    marginLeft: 8,
    fontSize: 14,
    color: '#666',
    fontFamily: 'Orpheus Pro',
  },
  rsvpSection: {
    marginTop: 15,
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  rsvpSectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#4c956c',
    marginBottom: 10,
    textAlign: 'center',
    fontFamily: fontFamilies.Bodoni72Smallcaps,
  },
  RSVPbutton: {
    paddingHorizontal: isMobile ? 20 : 15,
    paddingVertical: 10,
    backgroundColor: '#e0e0e0',
    borderRadius: 25,
    minWidth: 0,
    maxWidth: isMobile ? '100%' : 140,
    flexShrink: 1,
    marginVertical: isMobile ? 6 : 0,
    alignItems: 'center',
  },
  selectedButtonGreen: {
    paddingHorizontal: isMobile ? 20 : 15,
    paddingVertical: 10,
    backgroundColor: '#4c956c',
    borderRadius: 25,
    minWidth: 0,
    maxWidth: isMobile ? '100%' : 140,
    flexShrink: 1,
    marginVertical: isMobile ? 6 : 0,
    alignItems: 'center',
  },
  selectedButtonBeige: {
    paddingHorizontal: isMobile ? 20 : 15,
    paddingVertical: 10,
    backgroundColor: '#ffd700',
    borderRadius: 25,
    minWidth: 0,
    maxWidth: isMobile ? '100%' : 140,
    flexShrink: 1,
    marginVertical: isMobile ? 6 : 0,
    alignItems: 'center',
  },
  selectedButtonRed: {
    paddingHorizontal: isMobile ? 20 : 15,
    paddingVertical: 10,
    backgroundColor: '#ff6b6b',
    borderRadius: 25,
    minWidth: 0,
    maxWidth: isMobile ? '100%' : 140,
    flexShrink: 1,
    marginVertical: isMobile ? 6 : 0,
    alignItems: 'center',
  },
  RSVPButtonText: {
    color: '#000',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
    fontFamily: fontFamilies.Bodoni72Smallcaps,
  },
  viewResponsesButton: {
    backgroundColor: '#4c956c',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 20,
    alignItems: 'center',
    marginTop: 10,
  },
  viewResponsesButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  // Outline variant for owner top-row "View Responses"
  viewResponsesOutline: {
    borderWidth: 1,
    borderColor: '#4c956c',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: 'transparent',
    marginRight: 8,
    alignItems: 'center',
    justifyContent: 'center'
  },
  viewResponsesOutlineText: {
    color: '#4c956c',
    fontSize: 14,
    fontWeight: '600',
  },
  noEventMessage: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  noEventText: {
    fontSize: 16,
    color: '#777',
    textAlign: 'center',
    fontFamily: 'Orpheus Pro',
  },
  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalScrollView: {
    maxHeight: isMobile ? 400 : 500,
  },
  modalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
    marginTop: 15,
  },
  modalInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#fff',
    marginBottom: 10,
  },
  modalTextArea: {
    minHeight: 80,
    verticalAlign: 'top',
  },
  locationInputContainer: {
    position: 'relative',
  },
  locationSuggestions: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    maxHeight: 200,
    zIndex: 1000,
  },
  locationSuggestionItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  locationSuggestionText: {
    fontSize: 14,
    color: '#333',
  },
  toggleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 15,
  },
  toggle: {
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 15,
  },
  toggleActive: {
    backgroundColor: '#4c956c',
  },
  toggleInactive: {
    backgroundColor: '#e0e0e0',
  },
  toggleText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 20,
  },
  modalSaveButton: {
    flex: 1,
    backgroundColor: '#4c956c',
    padding: 14,
    borderRadius: 25,
    alignItems: 'center',
  },
  modalSaveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  modalCancelButton: {
    flex: 1,
    backgroundColor: '#e8e8e8',
    padding: 14,
    borderRadius: 25,
    alignItems: 'center',
  },
  modalCancelButtonText: {
    color: '#666',
    fontSize: 16,
    fontWeight: '600',
  },
  // RSVP Modal Styles
  rsvpModalContent: {
    maxHeight: 400,
  },
  rsvpTabs: {
    flexDirection: 'row',
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  rsvpTab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
  },
  activeRsvpTab: {
    borderBottomWidth: 2,
    borderBottomColor: '#4c956c',
  },
  rsvpTabText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  activeRsvpTabText: {
    color: '#4c956c',
    fontWeight: '600',
  },
  rsvpUsersList: {
    maxHeight: 300,
  },
  rsvpUserItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  rsvpUserName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  rsvpUserEmail: {
    fontSize: 14,
    color: '#666',
    marginTop: 2,
  },
  rsvpUserPhone: {
    fontSize: 13,
    color: '#4c956c',
    marginTop: 2,
  },
  noRsvpText: {
    textAlign: 'center',
    color: '#999',
    fontSize: 14,
    padding: 20,
  },
});

export default EventCard;
